import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Upload, Trash2, Filter, CalendarDays, List as ListIcon, Sparkles, Plus, X, Video, Image as ImageIcon, FileText, ChevronLeft, ChevronRight, Wand2, FolderOpen, Tag as TagIcon, Check, Eye, Heart, MessageCircle, Send, Bookmark, MoreHorizontal, Play, Film, Copy, Camera, Download, ArrowRight, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (path) => `${BACKEND_URL}/api/files/${path}`;

const PILARES = [
  { id: 1, label: "1 · Todavía existen playas así", color: "#E07A5F", short: "P1" },
  { id: 2, label: "2 · Así se siente descansar", color: "#81B29A", short: "P2" },
  { id: 3, label: "3 · Los pequeños lujos", color: "#F2CC8F", short: "P3" },
  { id: 4, label: "4 · La infancia", color: "#B08968", short: "P4" },
  { id: 5, label: "5 · La naturaleza", color: "#3D5A80", short: "P5" },
  { id: 6, label: "6 · Escápate", color: "#98467E", short: "P6" },
  { id: 7, label: "7 · Barra de Galindo", color: "#264653", short: "P7" },
];
const pilarInfo = (id) => PILARES.find((p) => p.id === Number(id)) || PILARES[0];

const CONTENT_TYPES = [
  { id: "reel", label: "Reel" },
  { id: "carousel", label: "Carrusel" },
  { id: "story", label: "Story" },
  { id: "post", label: "Post" },
];
const ESTADOS = [
  { id: "idea", label: "Idea", color: "#94A3B8" },
  { id: "borrador", label: "Borrador", color: "#F59E0B" },
  { id: "grabado", label: "Grabado", color: "#0EA5E9" },
  { id: "en_edicion", label: "En edición", color: "#8B5CF6" },
  { id: "programado", label: "Programado", color: "#22C55E" },
  { id: "publicado", label: "Publicado", color: "#059669" },
];
const estadoInfo = (id) => ESTADOS.find((e) => e.id === id) || ESTADOS[0];
const NEXT_ESTADO = {
  idea: "grabado",
  borrador: "grabado",
  grabado: "en_edicion",
  en_edicion: "programado",
  programado: "publicado",
};
const nextEstado = (id) => NEXT_ESTADO[id] || null;

const PROPS = [
  { id: "both", label: "Ambas" },
  { id: "casita", label: "La Casita" },
  { id: "loft", label: "Pura Brisa" },
  { id: "none", label: "Sin propiedad" },
];

const DEFAULT_HASHTAGS = [
  "#barradegalindo", "#playasdetuxpan", "#tuxpanveracruz", "#veracruzmexico",
  "#casasdeplaya", "#rentavacacional", "#viajarmexico", "#findesemanacdmx",
  "#escapadasenmexico", "#airbnbmexico", "#reels", "#mexico",
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

// ---------- Editor modal ----------
function ItemEditor({ item, token, taxonomy, onClose, onSaved, onPreview }) {
  const isNew = !item?.id;
  const [form, setForm] = useState(() => ({
    ig_content_type: item?.ig_content_type || "reel",
    pilar: item?.pilar || 2,
    hook: item?.hook || "",
    caption_es: item?.caption_es || "",
    caption_en: item?.caption_en || "",
    hashtags: item?.hashtags?.length ? item.hashtags : DEFAULT_HASHTAGS,
    guion: item?.guion || "",
    cta: item?.cta || "",
    property_id: item?.property_id || "both",
    estado: item?.estado || "idea",
    fecha_planeada: item?.fecha_planeada || todayISO(),
    duration_sec: item?.duration_sec || 0,
  }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showVisualGen, setShowVisualGen] = useState(false);
  const [asset, setAsset] = useState({
    storage_path: item?.storage_path,
    asset_type: item?.asset_type,
    content_type: item?.content_type,
  });
  const [attachedAssets, setAttachedAssets] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [showTextOverlay, setShowTextOverlay] = useState(false);
  const [generatingSlides, setGeneratingSlides] = useState(false);

  const generateCarouselSlides = async (style = "classic") => {
    if (isNew) { toast.error("Guarda primero el borrador"); return; }
    setGeneratingSlides(true);
    const styleLabel = { classic: "Clásico", elegant: "Elegante", minimal: "Minimal" }[style] || style;
    toast.info(`Generando slides estilo ${styleLabel}… (1-2 min)`);
    try {
      await api.post(`/admin/ig-media/${item.id}/generate-carousel`, { style }, { headers: { Authorization: `Bearer ${token}` } });
      const started = Date.now();
      const poll = async () => {
        if (Date.now() - started > 240000) {
          toast.error("La generación tardó demasiado. Recarga en un momento.");
          setGeneratingSlides(false);
          return;
        }
        try {
          const { data } = await api.get(`/admin/ig-media/${item.id}/carousel-status`, { headers: { Authorization: `Bearer ${token}` } });
          if (data.status === "done") {
            const it = data.item || {};
            setAsset({ storage_path: it.storage_path, asset_type: it.asset_type, content_type: it.content_type });
            const res = await api.get(`/admin/ig-media/${item.id}/attached-assets`, { headers: { Authorization: `Bearer ${token}` } });
            setAttachedAssets(res.data.items || []);
            onSaved(it);
            toast.success("Slides de carrusel generadas ✨");
            setGeneratingSlides(false);
            return;
          }
          if (data.status === "failed") {
            toast.error(data.error || "Falló la generación de slides");
            setGeneratingSlides(false);
            return;
          }
        } catch { /* keep polling */ }
        setTimeout(poll, 5000);
      };
      setTimeout(poll, 8000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar slides");
      setGeneratingSlides(false);
    }
  };

  // Load attached assets on mount
  useEffect(() => {
    if (!item?.id) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/ig-media/${item.id}/attached-assets`, { headers: { Authorization: `Bearer ${token}` } });
        setAttachedAssets(data.items || []);
      } catch { /* silent */ }
    })();
  }, [item?.id, token]);

  const attachedIds = useMemo(() => attachedAssets.map((a) => a.id), [attachedAssets]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.hook.trim()) { toast.error("El hook es obligatorio"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/admin/ig-media", form, { headers: { Authorization: `Bearer ${token}` } });
        onSaved(data);
        toast.success("Item creado");
      } else {
        const { data } = await api.put(`/admin/ig-media/${item.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        onSaved(data);
        toast.success("Cambios guardados");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const generateAI = async () => {
    if (!form.hook.trim()) { toast.error("Escribe primero el hook para generar caption"); return; }
    if (isNew) { toast.error("Guarda primero el borrador antes de generar caption con IA"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post(
        `/admin/ig-media/${item.id}/generate-caption`,
        { hook: form.hook, guion: form.guion, pilar: form.pilar, ig_content_type: form.ig_content_type, property_id: form.property_id, save: false, asset_ids: attachedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setForm((f) => ({
        ...f,
        caption_es: data.caption_es || f.caption_es,
        caption_en: data.caption_en || f.caption_en,
        hashtags: data.hashtags?.length ? data.hashtags : f.hashtags,
      }));
      toast.success("Caption generado con IA ✨");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar con IA");
    } finally { setGenerating(false); }
  };


  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isNew) {
      toast.error("Primero guarda el borrador antes de subir el archivo");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/ig-media/${item.id}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setAsset({ storage_path: data.storage_path, asset_type: data.asset_type, content_type: data.content_type });
      onSaved(data);
      toast.success("Archivo subido");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir");
    } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/70 backdrop-blur-sm z-[100] flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="ig-editor-modal">
      <div className="bg-white border border-stone-200 max-w-4xl w-full my-8 shadow-2xl">
        <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4 sticky top-0 bg-white z-10">
          <h3 className="font-serif-display text-2xl">{isNew ? "Nuevo item" : "Editar item"}</h3>
          <div className="flex items-center gap-3">
            {!isNew && form.ig_content_type === "carousel" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    disabled={generatingSlides}
                    data-testid="ig-editor-generate-carousel"
                    title="Genera portada + 4-6 slides + CTA con IA usando las fotos adjuntas como fondo"
                    className="inline-flex items-center gap-2 bg-[#E07A5F] hover:bg-[#C9654C] disabled:opacity-60 text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold"
                  >
                    {generatingSlides ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    {generatingSlides ? "Generando…" : "Generar slides"}
                    {!generatingSlides && <ChevronDown className="w-3 h-3 -ml-1" />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
                    Estilo tipográfico
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => generateCarouselSlides("elegant")}
                    data-testid="ig-editor-generate-carousel-elegant"
                    className="flex flex-col items-start gap-0.5 py-2.5 cursor-pointer"
                  >
                    <span className="font-serif-display text-base">Elegante</span>
                    <span className="text-[10px] text-stone-500">DM Serif Display · estilo revista</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => generateCarouselSlides("minimal")}
                    data-testid="ig-editor-generate-carousel-minimal"
                    className="flex flex-col items-start gap-0.5 py-2.5 cursor-pointer"
                  >
                    <span className="font-sans font-semibold text-base tracking-tight">Minimal</span>
                    <span className="text-[10px] text-stone-500">Outfit · foto protagonista</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => generateCarouselSlides("classic")}
                    data-testid="ig-editor-generate-carousel-classic"
                    className="flex flex-col items-start gap-0.5 py-2.5 cursor-pointer"
                  >
                    <span className="font-serif-display text-base">Clásico</span>
                    <span className="text-[10px] text-stone-500">Cormorant · terracota (actual)</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {!isNew && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    const res = await api.get(`/admin/ig-media/${item.id}/edit-brief.pdf`, {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: "blob",
                    });
                    const blob = new Blob([res.data], { type: "application/pdf" });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `guia-edicion-${(item.hook || "post").slice(0, 40).replace(/[^\w\-]+/g, "-")}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Guía de edición descargada");
                  } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "No se pudo generar el PDF";
                    toast.error(typeof msg === "string" ? msg : "No se pudo generar el PDF");
                  }
                }}
                data-testid="ig-editor-brief-pdf"
                title="Descargar guía de edición para este post"
                className="inline-flex items-center gap-2 bg-[#3D5A80] hover:bg-[#2C4560] text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold"
              >
                <Download className="w-4 h-4" /> Guía de edición (PDF)
              </button>
            )}
            <button onClick={onClose} data-testid="ig-editor-close" className="text-stone-500 hover:text-stone-900"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-6">
          {/* LEFT: media */}
          <div>
            <label className="text-xs uppercase tracking-wider text-stone-500 block mb-2">Archivo (foto o video)</label>
            {asset.storage_path ? (
              <div className="border border-stone-200 bg-stone-50 aspect-[9/16] max-h-96 overflow-hidden flex items-center justify-center relative">
                {asset.asset_type === "video" ? (
                  <video src={fileUrl(asset.storage_path)} controls className="w-full h-full object-contain" />
                ) : (
                  <img src={fileUrl(asset.storage_path)} alt="preview" className="w-full h-full object-contain" />
                )}
              </div>
            ) : (
              <div className="border border-dashed border-stone-300 aspect-[9/16] max-h-96 flex flex-col items-center justify-center text-stone-400 text-sm p-4 text-center">
                <ImageIcon className="w-8 h-8 mb-2" />
                {isNew ? "Guarda primero el borrador para poder subir archivo" : "Aún no hay archivo. Sube foto o video, o adjunta uno de la biblioteca y presiona \"Usar\"."}
              </div>
            )}
            <label className="mt-3 block">
              <input type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm" className="hidden" onChange={uploadFile} disabled={uploading || isNew} data-testid="ig-editor-file-input" />
              <span className={`cursor-pointer inline-flex items-center justify-center gap-2 w-full bg-stone-900 hover:bg-stone-700 text-white px-4 py-3 text-xs uppercase tracking-[0.15em] font-semibold ${uploading || isNew ? "opacity-50 pointer-events-none" : ""}`}>
                <Upload className="w-4 h-4" /> {uploading ? "Subiendo..." : asset.storage_path ? "Reemplazar archivo" : "Subir archivo"}
              </span>
            </label>
            {!isNew && asset.storage_path && (
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm("¿Quitar el archivo principal del post? Los assets adjuntos NO se borran.")) return;
                  try {
                    await api.post(`/admin/ig-media/${item.id}/clear-primary`, {}, { headers: { Authorization: `Bearer ${token}` } });
                    setAsset({ storage_path: null, asset_type: null, content_type: null });
                    onSaved({ ...item, storage_path: null, asset_type: null, content_type: null });
                    toast.success("Archivo quitado. Ya puedes aplicar otro desde la biblioteca o subir uno nuevo.");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Error al quitar archivo");
                  }
                }}
                data-testid="ig-editor-clear-primary-btn"
                className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-red-500 text-red-600 hover:bg-red-50 px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold transition-colors"
              >
                <Trash2 className="w-4 h-4" /> Quitar archivo principal
              </button>
            )}
            <p className="mt-2 text-[11px] text-stone-400">Formatos: JPG/PNG/WebP (máx 15MB) · MP4/MOV/WebM (máx 200MB)</p>

            {/* Add text layer to the current video */}
            {!isNew && asset.storage_path && asset.asset_type === "video" && (
              <button
                type="button"
                onClick={() => setShowTextOverlay(true)}
                data-testid="ig-editor-add-text-overlay"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-[#3D5A80] text-[#3D5A80] hover:bg-[#3D5A80] hover:text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold transition-colors"
              >
                <TagIcon className="w-4 h-4" /> Añadir capa de texto al video
              </button>
            )}

            {/* Visual AI generation */}
            {!isNew && (
              <div className="mt-4 border border-dashed border-[#3D5A80]/40 bg-[#3D5A80]/5 p-3">
                <div className="flex items-start gap-2">
                  <Film className="w-5 h-5 text-[#3D5A80] flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-wider font-semibold text-[#3D5A80]">Sin material propio?</p>
                    <p className="text-[11px] text-stone-600 leading-tight mb-2">Genera prompts hiperrealistas para Sora (video) y Nano Banana (imagen), o crea la imagen directamente.</p>
                    <button
                      type="button"
                      onClick={() => setShowVisualGen(true)}
                      data-testid="ig-editor-visual-gen"
                      className="text-xs bg-[#3D5A80] hover:bg-[#2C4159] text-white px-3 py-1.5 inline-flex items-center gap-1.5 uppercase tracking-wider font-semibold"
                    >
                      <Wand2 className="w-3.5 h-3.5" /> Generar visual con IA
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Attached assets from library */}
            {!isNew && (
              <div className="mt-6 border-t border-stone-200 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-wider text-stone-500">Assets adjuntos ({attachedAssets.length})</label>
                  <button type="button" onClick={() => setShowPicker(true)} data-testid="ig-editor-attach-btn" className="text-xs uppercase tracking-wider text-[#E07A5F] hover:text-[#D3674B] inline-flex items-center gap-1">
                    <FolderOpen className="w-3.5 h-3.5" /> Adjuntar de biblioteca
                  </button>
                </div>
                <p className="text-[11px] text-stone-400 mb-2">Los tags de estos assets se pasan a la IA como contexto al generar el caption. Presiona <strong>USAR</strong> en cualquiera para hacerlo el archivo principal del post (reemplaza el actual automáticamente).</p>
                {attachedAssets.length === 0 ? (
                  <p className="text-xs text-stone-400 italic py-2">Aún no hay assets adjuntos.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {attachedAssets.map((a) => {
                      const isPrimary = a.storage_path && a.storage_path === asset.storage_path;
                      return (
                      <div key={a.id} className={`relative aspect-square bg-stone-100 border ${isPrimary ? "border-[#E07A5F] ring-2 ring-[#E07A5F]/40" : "border-stone-200"}`}>
                        {a.asset_type === "video" ? (
                          <video src={fileUrl(a.storage_path)} className="w-full h-full object-cover" muted preload="metadata" />
                        ) : (
                          <img src={fileUrl(a.storage_path)} alt="" className="w-full h-full object-cover" />
                        )}
                        {a.asset_type === "video" && (
                          <span className="absolute top-1 left-1 bg-stone-900/80 text-white text-[9px] px-1.5 py-0.5 uppercase tracking-wider inline-flex items-center gap-0.5 pointer-events-none">
                            <Video className="w-2.5 h-2.5" /> Video
                          </span>
                        )}
                        {isPrimary && (
                          <span className="absolute top-1 right-1 bg-[#E07A5F] text-white text-[9px] px-1.5 py-0.5 uppercase tracking-wider font-semibold pointer-events-none">Principal</span>
                        )}
                        {/* Always-visible action bar at the bottom */}
                        <div className="absolute inset-x-0 bottom-0 bg-stone-900/90 flex items-stretch">
                          {!isPrimary && (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const { data: res } = await api.post(
                                    `/admin/ig-media/${item.id}/use-asset/${a.id}`,
                                    {},
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  setAsset({
                                    storage_path: res.item.storage_path,
                                    asset_type: res.item.asset_type,
                                    content_type: res.item.content_type,
                                  });
                                  onSaved(res.item);
                                  toast.success("Aplicado como archivo principal");
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Error al aplicar");
                                }
                              }}
                              data-testid={`ig-editor-use-primary-${a.id}`}
                              title="Usar como archivo principal del post"
                              className="flex-1 bg-[#E07A5F] hover:bg-[#c96549] text-white text-[10px] uppercase tracking-wider py-1.5 font-semibold inline-flex items-center justify-center gap-1"
                            >
                              <Check className="w-3 h-3" /> Usar
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setLightbox(a)}
                            data-testid={`ig-editor-preview-${a.id}`}
                            title="Ver a pantalla completa"
                            className="flex-1 text-white text-[10px] uppercase tracking-wider py-1.5 hover:bg-stone-700 inline-flex items-center justify-center gap-1"
                          >
                            <Eye className="w-3 h-3" /> Ver
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              const newIds = attachedIds.filter((id) => id !== a.id);
                              try {
                                await api.post(`/admin/ig-media/${item.id}/attach-assets`, { asset_ids: newIds }, { headers: { Authorization: `Bearer ${token}` } });
                                setAttachedAssets((prev) => prev.filter((x) => x.id !== a.id));
                              } catch { toast.error("Error al quitar"); }
                            }}
                            data-testid={`ig-editor-detach-${a.id}`}
                            title="Quitar de este post"
                            className="px-2 text-white hover:bg-red-600 inline-flex items-center justify-center"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        {/* Tags overlay hidden — action bar always visible now */}
                      </div>
                    );})}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: metadata */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Tipo</label>
                <select value={form.ig_content_type} onChange={(e) => patch("ig_content_type", e.target.value)} data-testid="ig-editor-type" className="w-full border border-stone-300 px-3 py-2 text-sm rounded-none">
                  {CONTENT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Estado</label>
                <select value={form.estado} onChange={(e) => patch("estado", e.target.value)} data-testid="ig-editor-estado" className="w-full border border-stone-300 px-3 py-2 text-sm rounded-none">
                  {ESTADOS.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Pilar</label>
                <select value={form.pilar} onChange={(e) => patch("pilar", parseInt(e.target.value))} data-testid="ig-editor-pilar" className="w-full border border-stone-300 px-3 py-2 text-sm rounded-none">
                  {PILARES.map((p) => <option key={p.id} value={p.id}>{p.short} — {p.label.substring(4)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Propiedad</label>
                <select value={form.property_id} onChange={(e) => patch("property_id", e.target.value)} data-testid="ig-editor-property" className="w-full border border-stone-300 px-3 py-2 text-sm rounded-none">
                  {PROPS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Fecha planeada</label>
                <input type="date" value={form.fecha_planeada || ""} onChange={(e) => patch("fecha_planeada", e.target.value)} data-testid="ig-editor-date" className="w-full border border-stone-300 px-3 py-2 text-sm rounded-none" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Duración (s)</label>
                <input type="number" min={0} max={90} value={form.duration_sec} onChange={(e) => patch("duration_sec", parseInt(e.target.value) || 0)} data-testid="ig-editor-duration" className="w-full border border-stone-300 px-3 py-2 text-sm rounded-none" />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Hook (0-2s) *</label>
              <input value={form.hook} onChange={(e) => patch("hook", e.target.value)} data-testid="ig-editor-hook" placeholder='Ej: "Esta playa sigue siendo un secreto"' className="w-full border border-stone-300 px-3 py-2 text-sm rounded-none font-medium" />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Guion / Notas de producción</label>
              <textarea value={form.guion} onChange={(e) => patch("guion", e.target.value)} data-testid="ig-editor-guion" rows={3} placeholder="Beats del reel, tomas, transiciones..." className="w-full border border-stone-300 px-3 py-2 text-sm rounded-none" />
            </div>

            <div className="border border-[#E07A5F]/30 bg-[#E07A5F]/5 p-3 flex items-center justify-between gap-3">
              <div className="text-xs text-stone-600 leading-tight">
                <p className="font-semibold text-[#E07A5F] uppercase tracking-wider text-[10px]">Copywriting con IA</p>
                <p>Genera caption ES + EN + 12 hashtags basados en el hook y el pilar, con voz de marca &quot;Playa Secreta&quot;.</p>
              </div>
              <Button onClick={generateAI} disabled={generating || isNew} data-testid="ig-editor-ai-btn" className="bg-[#E07A5F] hover:bg-[#D3674B] text-white rounded-none whitespace-nowrap">
                <Wand2 className="w-4 h-4 mr-2" /> {generating ? "Generando..." : "Generar con IA"}
              </Button>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Caption ES</label>
              <textarea value={form.caption_es} onChange={(e) => patch("caption_es", e.target.value)} data-testid="ig-editor-caption-es" rows={4} className="w-full border border-stone-300 px-3 py-2 text-sm rounded-none" />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Caption EN</label>
              <textarea value={form.caption_en} onChange={(e) => patch("caption_en", e.target.value)} data-testid="ig-editor-caption-en" rows={4} className="w-full border border-stone-300 px-3 py-2 text-sm rounded-none" />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">CTA</label>
              <input value={form.cta} onChange={(e) => patch("cta", e.target.value)} data-testid="ig-editor-cta" placeholder='"Guarda para tu próximo escape 🌊"' className="w-full border border-stone-300 px-3 py-2 text-sm rounded-none" />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Hashtags ({form.hashtags.length})</label>
              <textarea value={form.hashtags.join(" ")} onChange={(e) => patch("hashtags", e.target.value.split(/\s+/).filter(Boolean))} data-testid="ig-editor-hashtags" rows={2} className="w-full border border-stone-300 px-3 py-2 text-xs rounded-none font-mono" />
              <p className="mt-1 text-[11px] text-stone-400">Separados por espacio. Recomendado: 12 hashtags (4 capas: nicho super específico → nicho medio → destino amplio → emocional).</p>
            </div>
          </div>
        </div>

        <div className="border-t border-stone-200 px-6 py-4 flex justify-between items-center gap-3 sticky bottom-0 bg-white flex-wrap">
          {!isNew && onPreview && (
            <Button type="button" variant="outline" onClick={() => onPreview({ ...item, ...form })} data-testid="ig-editor-preview" className="rounded-none border-stone-400">
              <Eye className="w-4 h-4 mr-2" /> Vista previa
            </Button>
          )}
          <div className="ml-auto flex gap-3">
            <Button variant="outline" onClick={onClose} className="rounded-none">Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="ig-editor-save" className="bg-[#E07A5F] hover:bg-[#D3674B] text-white rounded-none px-6">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </div>
      </div>

      {showPicker && (
        <AssetPickerModal
          token={token}
          taxonomy={taxonomy}
          initialSelection={attachedIds}
          onClose={() => setShowPicker(false)}
          onConfirm={async (ids) => {
            try {
              await api.post(`/admin/ig-media/${item.id}/attach-assets`, { asset_ids: ids }, { headers: { Authorization: `Bearer ${token}` } });
              const { data } = await api.get(`/admin/ig-media/${item.id}/attached-assets`, { headers: { Authorization: `Bearer ${token}` } });
              setAttachedAssets(data.items || []);
              setShowPicker(false);
              toast.success(`${ids.length} asset(s) adjuntos`);
            } catch (e) { toast.error(e?.response?.data?.detail || "Error al adjuntar"); }
          }}
        />
      )}
      {showVisualGen && (
        <VisualGenerator
          item={item}
          token={token}
          onClose={() => setShowVisualGen(false)}
          onGenerated={(updated) => {
            setAsset({ storage_path: updated.storage_path, asset_type: updated.asset_type, content_type: updated.content_type });
            onSaved(updated);
          }}
        />
      )}
      {lightbox && <AssetPreview asset={lightbox} onClose={() => setLightbox(null)} />}
      {showTextOverlay && (
        <TextOverlayDialog
          item={item}
          token={token}
          defaultText={form.hook || ""}
          onClose={() => setShowTextOverlay(false)}
          onApplied={(updated) => {
            setAsset({
              storage_path: updated.storage_path,
              asset_type: updated.asset_type,
              content_type: updated.content_type,
            });
            onSaved(updated);
            setShowTextOverlay(false);
          }}
        />
      )}
    </div>
  );
}

// ---------- List View ----------
function ListView({ items, onEdit, onDelete, onPreview, onQuickStatus, token }) {
  const [briefLoadingId, setBriefLoadingId] = useState(null);
  const downloadBrief = async (item) => {
    try {
      setBriefLoadingId(item.id);
      const res = await api.get(`/admin/ig-media/${item.id}/edit-brief.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `guia-edicion-${(item.hook || "post").slice(0, 40).replace(/[^\w\-]+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "No se pudo generar el PDF";
      toast.error(typeof msg === "string" ? msg : "No se pudo generar el PDF");
    } finally {
      setBriefLoadingId(null);
    }
  };
  const [mediaLoadingId, setMediaLoadingId] = useState(null);
  const downloadMedia = async (item) => {
    const isCarousel = item.ig_content_type === "carousel";
    setMediaLoadingId(item.id);
    try {
      const endpoint = isCarousel
        ? `/admin/ig-media/${item.id}/slides.zip`
        : `/admin/ig-media/${item.id}/download-media`;
      const res = await api.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const disp = res.headers["content-disposition"] || "";
      const m = disp.match(/filename="([^"]+)"/);
      const slug = (item.hook || "post").slice(0, 40).replace(/[^\w\-]+/g, "-").toLowerCase();
      const fallback = isCarousel ? `carrusel-${slug}.zip` : `${slug}.mp4`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : fallback;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(isCarousel ? "Slides del carrusel descargadas" : "Archivo descargado");
    } catch (e) {
      const msg = e?.response?.status === 404
        ? (isCarousel ? "Este post no tiene slides adjuntas" : "Este post no tiene archivo principal")
        : "No se pudo descargar";
      toast.error(msg);
    } finally {
      setMediaLoadingId(null);
    }
  };
  if (!items.length) {
    return <p className="text-center text-stone-400 py-16 text-sm">No hay items con estos filtros.</p>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {items.map((it) => {
        const pil = pilarInfo(it.pilar);
        const est = estadoInfo(it.estado);
        return (
          <div key={it.id} data-testid={`ig-card-${it.id}`} className="group border border-stone-200 bg-white hover:shadow-md transition-shadow flex flex-col">
            <button onClick={() => onEdit(it)} className="text-left">
              <div className="aspect-[9/16] bg-stone-100 relative overflow-hidden">
                {it.storage_path ? (
                  it.asset_type === "video" ? (
                    <video src={fileUrl(it.storage_path)} className="w-full h-full object-cover" muted preload="metadata" />
                  ) : (
                    <img src={fileUrl(it.storage_path)} alt={it.hook} className="w-full h-full object-cover" />
                  )
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-stone-300 p-4 text-center">
                    <FileText className="w-8 h-8 mb-2" />
                    <span className="text-[11px] uppercase tracking-wider">Sin archivo</span>
                  </div>
                )}
                <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider px-2 py-1 font-semibold text-white" style={{ background: pil.color }}>
                  {pil.short}
                </span>
                <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider px-2 py-1 font-semibold text-white" style={{ background: est.color }}>
                  {est.label}
                </span>
                {it.asset_type === "video" && (
                  <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider px-2 py-1 font-semibold text-white bg-stone-900/80 flex items-center gap-1">
                    <Video className="w-3 h-3" /> {it.duration_sec > 0 ? `${it.duration_sec}s` : "video"}
                  </span>
                )}
                {it.video_generation_status === "pending" && (
                  <div className="absolute inset-0 bg-[#3D5A80]/70 flex flex-col items-center justify-center text-white p-3 text-center" data-testid={`ig-card-video-pending-${it.id}`}>
                    <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full mb-2" />
                    <p className="text-[11px] uppercase tracking-wider font-semibold">Sora está generando…</p>
                    <p className="text-[10px] opacity-80 mt-1">2-5 min</p>
                  </div>
                )}
                {it.video_generation_status === "failed" && (
                  <span className="absolute bottom-2 right-2 text-[10px] uppercase tracking-wider px-2 py-1 font-semibold text-white bg-red-600" data-testid={`ig-card-video-failed-${it.id}`}>
                    Video falló
                  </span>
                )}
              </div>
              <div className="p-3">
                <p className="text-[11px] text-stone-400 uppercase tracking-wider mb-1">
                  {it.fecha_planeada || "sin fecha"} · {it.ig_content_type}
                </p>
                <p className="text-sm font-medium leading-snug line-clamp-2">{it.hook}</p>
              </div>
            </button>
            {(() => {
              const nxt = nextEstado(it.estado);
              if (!nxt) return (
                <div className="flex items-center justify-center gap-1.5 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-[#059669] bg-[#059669]/10 border-t border-stone-100" data-testid={`ig-status-done-${it.id}`}>
                  <Check className="w-3.5 h-3.5" /> Publicado
                </div>
              );
              const nxtInfo = estadoInfo(nxt);
              return (
                <button
                  onClick={() => onQuickStatus(it, nxt)}
                  data-testid={`ig-quick-status-${it.id}`}
                  title={`Marcar como ${nxtInfo.label.toLowerCase()}`}
                  className="flex items-center justify-center gap-1.5 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-white border-t border-stone-100 transition-opacity hover:opacity-85"
                  style={{ background: nxtInfo.color }}
                >
                  <ArrowRight className="w-3.5 h-3.5" /> Marcar {nxtInfo.label.toLowerCase()}
                </button>
              );
            })()}
            <div className="mt-auto flex border-t border-stone-100">
              <button onClick={() => onEdit(it)} className="flex-1 text-xs uppercase tracking-wider py-2 hover:bg-stone-50">Editar</button>
              <button
                onClick={() => downloadBrief(it)}
                disabled={briefLoadingId === it.id}
                data-testid={`ig-brief-pdf-${it.id}`}
                title="Descargar guía de edición (PDF)"
                className="px-3 text-[10px] uppercase tracking-wider font-semibold text-white bg-[#3D5A80] hover:bg-[#2C4560] disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {briefLoadingId === it.id ? (
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                <span>PDF</span>
              </button>
              <button
                onClick={() => downloadMedia(it)}
                disabled={mediaLoadingId === it.id}
                data-testid={`ig-download-media-${it.id}`}
                title={it.ig_content_type === "carousel" ? "Descargar slides del carrusel (ZIP)" : "Descargar reel/imagen para publicar"}
                className="px-3 text-[10px] uppercase tracking-wider font-semibold text-white bg-[#E07A5F] hover:bg-[#C9654C] disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {mediaLoadingId === it.id ? (
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                <span>{it.ig_content_type === "carousel" ? "ZIP" : it.asset_type === "video" ? "MP4" : "IMG"}</span>
              </button>
              <button onClick={() => onPreview(it)} data-testid={`ig-preview-${it.id}`} title="Ver como se verá en Instagram" className="px-4 text-stone-400 hover:text-[#E07A5F] hover:bg-[#E07A5F]/5"><Eye className="w-4 h-4" /></button>
              <button onClick={() => onDelete(it)} data-testid={`ig-delete-${it.id}`} className="px-4 text-stone-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Calendar Post Preview (with thumb + slides navigator) ----------
function CalendarPostThumb({ item, onEdit, onDragStart, pil }) {
  const [slideIdx, setSlideIdx] = useState(0);
  const slides = item.slide_thumbs || [];
  const primaryPath = item.storage_path;
  const primaryCT = item.content_type || "";
  const isReel = item.ig_content_type === "reel" || primaryCT.startsWith("video/");
  const isCarousel = item.ig_content_type === "carousel" && slides.length > 0;
  const isPost = !isCarousel && !isReel;

  // Determinar thumbnail visible
  let thumbUrl = "";
  if (isCarousel) {
    const s = slides[slideIdx] || slides[0];
    if (s?.storage_path) thumbUrl = fileUrl(s.storage_path);
  } else if (primaryPath) {
    // Reels y posts: usa storage_path. Si es video, el frontend no puede thumbnailear directo — usa placeholder.
    if (isReel) {
      // Si hay slide_thumbs (cover generado), úsala; si no, no mostramos img
      thumbUrl = slides[0]?.storage_path ? fileUrl(slides[0].storage_path) : "";
    } else {
      thumbUrl = fileUrl(primaryPath);
    }
  } else if (slides[0]?.storage_path) {
    thumbUrl = fileUrl(slides[0].storage_path);
  }

  const nav = (e, delta) => {
    e.stopPropagation();
    setSlideIdx((i) => (i + delta + slides.length) % slides.length);
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, item)}
      onClick={() => onEdit(item)}
      data-testid={`ig-cal-post-${item.id}`}
      className="group relative overflow-hidden cursor-move border border-stone-200 hover:ring-2 hover:ring-[#E07A5F]/60 transition-all bg-stone-100"
      style={{ borderLeft: `3px solid ${pil.color}` }}
      title={item.hook}
    >
      {/* Thumbnail (o placeholder según tipo) */}
      <div className="relative aspect-square w-full bg-stone-200 overflow-hidden">
        {thumbUrl ? (
          <img src={thumbUrl} alt={item.hook} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-stone-100 to-stone-300 text-stone-500">
            {isReel ? <Film className="w-5 h-5" /> : isCarousel ? <ImageIcon className="w-5 h-5" /> : <Camera className="w-5 h-5" />}
          </div>
        )}

        {/* Overlay tipo */}
        {isReel && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/10 transition-colors" data-testid={`ig-cal-reel-play-${item.id}`}>
            <div className="bg-white/95 rounded-full p-1.5 shadow-lg">
              <Play className="w-3.5 h-3.5 text-stone-900 fill-stone-900 translate-x-[1px]" />
            </div>
          </div>
        )}

        {/* Contador de slides (carrusel) */}
        {isCarousel && slides.length > 1 && (
          <div className="absolute top-0.5 right-0.5 bg-black/70 text-white text-[9px] font-bold px-1 py-0.5 rounded-sm flex items-center gap-0.5" data-testid={`ig-cal-slide-counter-${item.id}`}>
            <ImageIcon className="w-2.5 h-2.5" />
            {slideIdx + 1}/{slides.length}
          </div>
        )}

        {/* Flechas de navegación (carrusel · solo si >1 slide) */}
        {isCarousel && slides.length > 1 && (
          <>
            <button
              onClick={(e) => nav(e, -1)}
              className="absolute left-0.5 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white text-stone-800 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow"
              aria-label="Slide anterior"
              data-testid={`ig-cal-slide-prev-${item.id}`}
            >
              <ChevronLeft className="w-3 h-3" />
            </button>
            <button
              onClick={(e) => nav(e, 1)}
              className="absolute right-0.5 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white text-stone-800 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow"
              aria-label="Slide siguiente"
              data-testid={`ig-cal-slide-next-${item.id}`}
            >
              <ChevronRight className="w-3 h-3" />
            </button>
          </>
        )}

        {/* Badge tipo (esquina inferior izquierda) */}
        <div className="absolute bottom-0.5 left-0.5 bg-white/95 text-[8px] font-bold uppercase tracking-wider px-1 py-0.5" style={{ color: pil.color }}>
          {item.ig_content_type[0].toUpperCase()}
        </div>
      </div>

      {/* Hook truncado */}
      <div className="px-1 py-1 text-[9px] leading-tight text-stone-700 line-clamp-2 bg-white">
        {item.hook}
      </div>
    </div>
  );
}


// ---------- Calendar View ----------
function CalendarView({ items, month, setMonth, onEdit, onReschedule }) {
  const [year, m] = month.split("-").map(Number);
  const firstDay = new Date(year, m - 1, 1);
  const lastDay = new Date(year, m, 0);
  const daysInMonth = lastDay.getDate();
  // getDay: 0=Sun..6=Sat. We want Mon-first.
  const startWeekday = (firstDay.getDay() + 6) % 7;
  const [dragOverDay, setDragOverDay] = useState(null);

  const byDay = useMemo(() => {
    const map = {};
    for (const it of items) {
      // Soporta fecha_planeada (posts sembrados) y publish_date (posts generados por Estudio IA)
      const fecha = it.fecha_planeada || it.publish_date;
      if (!fecha || !fecha.startsWith(month)) continue;
      const d = parseInt(fecha.slice(8, 10));
      if (!map[d]) map[d] = [];
      map[d].push(it);
    }
    return map;
  }, [items, month]);

  const shiftMonth = (delta) => {
    const dt = new Date(year, m - 1 + delta, 1);
    setMonth(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`);
  };

  const monthLabel = new Date(year, m - 1, 1).toLocaleDateString("es-MX", { month: "long", year: "numeric" });

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const onPostDragStart = (e, item) => {
    const currentDate = item.fecha_planeada || item.publish_date || null;
    e.dataTransfer.setData("application/json", JSON.stringify({ id: item.id, from: currentDate }));
    e.dataTransfer.effectAllowed = "move";
  };

  const onDayDrop = async (e, day) => {
    e.preventDefault();
    setDragOverDay(null);
    try {
      const payload = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
      if (!payload.id) return;
      const newDate = `${year}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      if (payload.from === newDate) return;
      await onReschedule(payload.id, newDate);
    } catch { /* silent */ }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => shiftMonth(-1)} data-testid="ig-cal-prev" className="p-2 hover:bg-stone-100 rounded-none"><ChevronLeft className="w-5 h-5" /></button>
        <div className="text-center">
          <h4 className="font-serif-display text-2xl capitalize">{monthLabel}</h4>
          <p className="text-[11px] text-stone-400 italic mt-1">Arrastra un post a otro día para reprogramarlo · pasa el mouse sobre un carrusel para ver todas las slides</p>
        </div>
        <button onClick={() => shiftMonth(1)} data-testid="ig-cal-next" className="p-2 hover:bg-stone-100 rounded-none"><ChevronRight className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-7 gap-px bg-stone-200 border border-stone-200">
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => (
          <div key={d} className="bg-stone-100 text-[11px] uppercase tracking-wider text-stone-500 text-center py-2 font-semibold">{d}</div>
        ))}
        {cells.map((day, idx) => {
          if (day === null) return <div key={idx} className="bg-stone-50 min-h-32" />;
          const dayItems = byDay[day] || [];
          const isDragOver = dragOverDay === day;
          return (
            <div
              key={idx}
              data-testid={`ig-cal-day-${day}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverDay(day); }}
              onDragLeave={() => setDragOverDay((prev) => prev === day ? null : prev)}
              onDrop={(e) => onDayDrop(e, day)}
              className={`bg-white min-h-32 p-1 flex flex-col gap-1 ${isDragOver ? "ring-2 ring-[#E07A5F] ring-inset bg-[#E07A5F]/5" : ""}`}
            >
              <div className="text-[11px] text-stone-400 font-mono px-0.5">{day}</div>
              {dayItems.slice(0, 2).map((it) => (
                <CalendarPostThumb
                  key={it.id}
                  item={it}
                  onEdit={onEdit}
                  onDragStart={onPostDragStart}
                  pil={pilarInfo(it.pilar)}
                />
              ))}
              {dayItems.length > 2 && (
                <button
                  onClick={() => onEdit(dayItems[2])}
                  className="text-[10px] text-stone-500 hover:text-[#E07A5F] font-semibold uppercase tracking-wider text-center py-1 border border-dashed border-stone-300 hover:border-[#E07A5F]"
                >
                  +{dayItems.length - 2} más
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ---------- Instagram-style post preview ----------
function formatCaptionForPreview(caption, cta, hashtags) {
  const parts = [];
  if (caption) parts.push(caption.trim());
  if (cta && !caption?.includes(cta)) parts.push(cta.trim());
  if (hashtags?.length) parts.push(hashtags.join(" "));
  return parts.filter(Boolean).join("\n\n");
}

function IGPostPreview({ item, token, onClose }) {
  const [lang, setLang] = useState("es");
  const [expanded, setExpanded] = useState(false);
  const [attachedAssets, setAttachedAssets] = useState([]);
  const [carouselIdx, setCarouselIdx] = useState(0);

  useEffect(() => {
    if (!item?.id) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/ig-media/${item.id}/attached-assets`, { headers: { Authorization: `Bearer ${token}` } });
        setAttachedAssets(data.items || []);
      } catch { /* silent */ }
    })();
  }, [item?.id, token]);

  // Pick media source: primary storage_path first, else attached assets carousel
  const primaryMedia = item?.storage_path
    ? [{ id: "primary", storage_path: item.storage_path, asset_type: item.asset_type, content_type: item.content_type }]
    : [];
  const mediaList = primaryMedia.length ? primaryMedia : attachedAssets;
  const currentMedia = mediaList[carouselIdx] || null;

  const captionText = lang === "es" ? item?.caption_es : item?.caption_en;
  const fullCaption = formatCaptionForPreview(captionText, item?.cta, item?.hashtags);
  const shortCaption = (item?.hook || "").trim();

  const isReel = item?.ig_content_type === "reel";
  const aspectClass = isReel ? "aspect-[9/16]" : "aspect-square";

  const pil = pilarInfo(item?.pilar);

  return (
    <div className="fixed inset-0 bg-stone-900/80 z-[130] flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="ig-post-preview" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="max-w-sm w-full my-6">
        {/* Header controls */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-1 bg-white/10 rounded-full p-0.5">
            <button onClick={() => setLang("es")} data-testid="ig-preview-lang-es" className={`px-3 py-1 text-xs uppercase tracking-wider rounded-full ${lang === "es" ? "bg-white text-stone-900" : "text-white/70 hover:text-white"}`}>ES</button>
            <button onClick={() => setLang("en")} data-testid="ig-preview-lang-en" className={`px-3 py-1 text-xs uppercase tracking-wider rounded-full ${lang === "en" ? "bg-white text-stone-900" : "text-white/70 hover:text-white"}`}>EN</button>
          </div>
          <button onClick={onClose} data-testid="ig-preview-close" className="text-white/70 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Instagram post mockup */}
        <div className="bg-white rounded-lg overflow-hidden shadow-2xl">
          {/* Post header */}
          <div className="flex items-center justify-between px-3 py-2.5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 p-[2px]">
                <div className="w-full h-full rounded-full bg-white p-[2px]">
                  <div className="w-full h-full rounded-full bg-gradient-to-br from-[#E07A5F] to-[#3D5A80] flex items-center justify-center text-white text-[10px] font-bold">V</div>
                </div>
              </div>
              <div className="leading-tight">
                <p className="text-[13px] font-semibold text-stone-900">villaspuravida.tuxpan</p>
                <p className="text-[10px] text-stone-500">Barra de Galindo, Veracruz</p>
              </div>
            </div>
            <MoreHorizontal className="w-5 h-5 text-stone-600" />
          </div>

          {/* Media */}
          <div className={`bg-stone-900 relative ${aspectClass} overflow-hidden`}>
            {currentMedia ? (
              currentMedia.asset_type === "video" ? (
                <video src={fileUrl(currentMedia.storage_path)} controls autoPlay muted loop playsInline className="w-full h-full object-cover" />
              ) : (
                <img src={fileUrl(currentMedia.storage_path)} alt="" className="w-full h-full object-cover" />
              )
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-stone-500 p-6 text-center">
                <ImageIcon className="w-12 h-12 mb-3 opacity-40" />
                <p className="text-sm">Aún no hay imagen o video adjunto.</p>
                <p className="text-xs mt-1 opacity-70">Sube un archivo al post o adjunta assets de la biblioteca.</p>
              </div>
            )}
            {/* Reel play indicator */}
            {isReel && currentMedia && (
              <div className="absolute top-3 right-3 flex items-center gap-1 bg-stone-900/50 backdrop-blur-sm px-2 py-1 rounded">
                <Play className="w-3 h-3 text-white fill-white" />
                <span className="text-white text-[10px] font-medium">Reel</span>
              </div>
            )}
            {/* Carousel dots */}
            {mediaList.length > 1 && (
              <>
                {carouselIdx > 0 && (
                  <button onClick={() => setCarouselIdx((i) => Math.max(0, i - 1))} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-1 hover:bg-white">
                    <ChevronLeft className="w-4 h-4 text-stone-800" />
                  </button>
                )}
                {carouselIdx < mediaList.length - 1 && (
                  <button onClick={() => setCarouselIdx((i) => Math.min(mediaList.length - 1, i + 1))} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-1 hover:bg-white">
                    <ChevronRight className="w-4 h-4 text-stone-800" />
                  </button>
                )}
                <div className="absolute top-3 left-1/2 -translate-x-1/2 flex gap-1">
                  {mediaList.map((_, i) => (
                    <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === carouselIdx ? "bg-white" : "bg-white/40"}`} />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Action bar */}
          <div className="px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Heart className="w-6 h-6 text-stone-900" />
              <MessageCircle className="w-6 h-6 text-stone-900" />
              <Send className="w-6 h-6 text-stone-900" />
            </div>
            <Bookmark className="w-6 h-6 text-stone-900" />
          </div>

          {/* Engagement + caption */}
          <div className="px-3 pb-3 space-y-1">
            <p className="text-[13px] font-semibold text-stone-900">1,247 me gusta</p>
            <div className="text-[13px] text-stone-900 leading-snug">
              <span className="font-semibold">villaspuravida.tuxpan </span>
              {expanded ? (
                <span className="whitespace-pre-wrap">{fullCaption || shortCaption || (lang === "es" ? "(sin caption)" : "(no caption)")}</span>
              ) : (
                <>
                  <span>{shortCaption}</span>
                  {(fullCaption && fullCaption !== shortCaption) && (
                    <>{" "}<button onClick={() => setExpanded(true)} className="text-stone-500 hover:text-stone-700">... más</button></>
                  )}
                </>
              )}
              {expanded && (
                <>{" "}<button onClick={() => setExpanded(false)} className="text-stone-500 hover:text-stone-700">ver menos</button></>
              )}
            </div>
            <button className="text-[13px] text-stone-500">Ver los 89 comentarios</button>
            <p className="text-[10px] uppercase tracking-wider text-stone-400 pt-1">HACE 2 HORAS</p>
          </div>
        </div>

        {/* Metadata footer */}
        <div className="mt-3 text-white/70 text-xs flex flex-wrap items-center gap-2">
          <span className="px-2 py-0.5" style={{ background: `${pil.color}30`, color: pil.color }}>{pil.short}</span>
          <span>·</span>
          <span className="uppercase tracking-wider">{item?.ig_content_type}</span>
          <span>·</span>
          <span>{item?.fecha_planeada || "sin fecha"}</span>
          {item?.duration_sec > 0 && <><span>·</span><span>{item.duration_sec}s</span></>}
          <span className="ml-auto italic opacity-70">Vista previa · no publicado</span>
        </div>
      </div>
    </div>
  );
}


// ---------- Visual Generator (Sora + Nano Banana prompts + one-click image gen) ----------
function VisualGenerator({ item, token, onClose, onGenerated }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [genImgLoading, setGenImgLoading] = useState(false);
  const [copied, setCopied] = useState("");
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [variantCount, setVariantCount] = useState(1);
  const [videoDuration, setVideoDuration] = useState(8);
  const [videoModel, setVideoModel] = useState("sora-2");
  const [genVideoLoading, setGenVideoLoading] = useState(false);
  const [videoStatus, setVideoStatus] = useState(null); // idle | pending | done | failed
  const [videoElapsed, setVideoElapsed] = useState(0); // seconds since start
  // Brand text overlay (applied after Sora finishes, using Cormorant Garamond / Outfit)
  const [overlayEnabled, setOverlayEnabled] = useState(true);
  const [overlayPosition, setOverlayPosition] = useState("bottom-safe"); // top | center | bottom | bottom-safe
  const [overlayFont, setOverlayFont] = useState("serif"); // serif | sans
  const [overlayHook, setOverlayHook] = useState("");
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTsRef = useRef(null);

  useEffect(() => {
    if (!item?.id) return;
    (async () => {
      try {
        const { data } = await api.post(
          `/admin/ig-media/${item.id}/generate-visual-prompt`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error generando prompts");
      } finally {
        setLoading(false);
      }
    })();
  }, [item?.id, token]);

  const copyPrompt = async (which, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(""), 2000);
      toast.success("Prompt copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const generateImageNow = async () => {
    if (!data?.prompt_nano_banana) return;
    setGenImgLoading(true);
    setVariants([]);
    setSelectedVariant(null);
    try {
      const { data: res } = await api.post(
        `/admin/ig-media/${item.id}/generate-image`,
        { prompt: data.prompt_nano_banana, save_as_primary: variantCount === 1, n_variants: variantCount },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 }
      );
      if (variantCount === 1) {
        toast.success("Imagen generada y guardada como archivo principal");
        onGenerated && onGenerated(res.item);
        onClose();
      } else {
        setVariants(res.variants || []);
        toast.success(`${(res.variants || []).length} variantes generadas`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar imagen (puede tardar hasta 2 min con 4 variantes)");
    } finally {
      setGenImgLoading(false);
    }
  };

  const selectVariant = async (v) => {
    setSelectedVariant(v.storage_path);
    try {
      const { data: res } = await api.post(
        `/admin/ig-media/${item.id}/select-variant`,
        { storage_path: v.storage_path, content_type: v.content_type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Imagen seleccionada y guardada");
      onGenerated && onGenerated(res.item);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al seleccionar");
      setSelectedVariant(null);
    }
  };

  // Detect existing pending video generation when opening the modal
  useEffect(() => {
    if (!item?.id) return;
    setOverlayHook(item.hook || "");
    if (item.video_generation_status === "pending") {
      setVideoStatus("pending");
      setGenVideoLoading(true);
      startVideoPolling();
    } else if (item.video_generation_status === "failed") {
      setVideoStatus("failed");
    }
    return () => {
      stopVideoPolling();
    };
  }, [item?.id]);

  const stopVideoPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
  };

  const startVideoPolling = () => {
    stopVideoPolling();
    startTsRef.current = Date.now();
    setVideoElapsed(0);
    elapsedRef.current = setInterval(() => {
      setVideoElapsed(Math.floor((Date.now() - (startTsRef.current || Date.now())) / 1000));
    }, 1000);
    pollRef.current = setInterval(async () => {
      try {
        const { data: st } = await api.get(
          `/admin/ig-media/${item.id}/video-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (st.status === "done") {
          stopVideoPolling();
          setVideoStatus("done");
          setGenVideoLoading(false);
          toast.success("Video generado y guardado como archivo principal");
          onGenerated && onGenerated(st.item);
          onClose();
        } else if (st.status === "failed") {
          stopVideoPolling();
          setVideoStatus("failed");
          setGenVideoLoading(false);
          toast.error(st.error || "La generación del video falló");
        }
        // else keep polling (pending)
      } catch (e) {
        // On 404 or auth issues, stop polling
        const s = e?.response?.status;
        if (s === 404 || s === 401 || s === 403) {
          stopVideoPolling();
          setGenVideoLoading(false);
          setVideoStatus("failed");
          toast.error(e?.response?.data?.detail || "No se pudo consultar el estado del video");
        }
      }
    }, 5000);
  };

  const generateVideoNow = async () => {
    if (!data?.prompt_sora) return;
    if (!item?.id) { toast.error("Guarda primero el post antes de generar video"); return; }
    if (!window.confirm(`Generar video con Sora 2 (${videoDuration}s, ${videoModel}).\n\nEsto tarda 2-5 minutos y consume créditos. Puedes cerrar el modal — te avisaremos cuando termine.\n\n¿Continuar?`)) return;
    setGenVideoLoading(true);
    setVideoStatus("pending");
    try {
      await api.post(
        `/admin/ig-media/${item.id}/generate-video`,
        {
          prompt: data.prompt_sora,
          duration: videoDuration,
          model: videoModel,
          save_as_primary: true,
          overlay_text: overlayEnabled,
          overlay_position: overlayPosition,
          overlay_font: overlayFont,
          overlay_hook_override: overlayHook !== (item?.hook || "") ? overlayHook : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.info(`Generación iniciada. Sora tarda ~${videoDuration <= 4 ? "2-3" : videoDuration <= 8 ? "3-5" : "5-8"} min. Puedes seguir trabajando.`);
      startVideoPolling();
    } catch (e) {
      setGenVideoLoading(false);
      setVideoStatus("failed");
      const detail = e?.response?.data?.detail;
      if (detail === "Item no encontrado") {
        toast.error("El post ya no existe en la base de datos. Cierra el modal, recarga la página y crea el post de nuevo.");
      } else if (e?.response?.status === 409) {
        toast.warning("Ya hay una generación en curso para este item");
        setVideoStatus("pending");
        setGenVideoLoading(true);
        startVideoPolling();
      } else {
        toast.error(detail || "Error al iniciar la generación de video");
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/80 z-[125] flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="ig-visual-gen-modal">
      <div className="bg-white max-w-4xl w-full my-4 max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4 sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-serif-display text-2xl">Generar visual con IA</h3>
            <p className="text-xs text-stone-500 mt-1">Prompts hiperrealistas basados en el hook, el pilar y la biblioteca visual</p>
          </div>
          <button onClick={onClose} data-testid="ig-visual-gen-close" className="text-stone-500 hover:text-stone-900"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="p-16 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-[#E07A5F] border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-sm text-stone-500">Analizando hook, pilar y biblioteca visual...</p>
            <p className="text-xs text-stone-400 mt-2 italic">(15-20 segundos)</p>
          </div>
        ) : !data ? (
          <div className="p-8 text-center text-stone-500">No se pudo generar. Reintenta.</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Nano Banana section */}
            <div className="border border-stone-200 bg-stone-50">
              <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-white">
                <div className="flex items-center gap-2">
                  <Camera className="w-5 h-5 text-[#E07A5F]" />
                  <h4 className="font-semibold text-sm">Nano Banana — Imagen fija</h4>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    onClick={() => copyPrompt("nano", data.prompt_nano_banana)}
                    data-testid="ig-visual-copy-nano"
                    className="text-xs uppercase tracking-wider text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
                  >
                    <Copy className="w-3.5 h-3.5" /> {copied === "nano" ? "Copiado" : "Copiar"}
                  </button>
                  <div className="flex gap-0 border border-stone-300">
                    <button onClick={() => setVariantCount(1)} data-testid="ig-visual-variant-1" className={`px-2 py-1 text-[11px] uppercase tracking-wider ${variantCount === 1 ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}>1 img</button>
                    <button onClick={() => setVariantCount(4)} data-testid="ig-visual-variant-4" className={`px-2 py-1 text-[11px] uppercase tracking-wider ${variantCount === 4 ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}>4 variantes</button>
                  </div>
                  <Button
                    onClick={generateImageNow}
                    disabled={genImgLoading}
                    data-testid="ig-visual-gen-image-btn"
                    className="bg-[#E07A5F] hover:bg-[#D3674B] text-white rounded-none text-xs"
                  >
                    {genImgLoading ? (variantCount === 4 ? "Generando 4... (60-120s)" : "Generando... (30-60s)") : (variantCount === 4 ? "Crear 4 variantes" : "Crear imagen ahora")}
                  </Button>
                </div>
              </div>
              <div className="p-4 max-h-56 overflow-y-auto text-xs text-stone-700 font-mono whitespace-pre-wrap leading-relaxed">
                {data.prompt_nano_banana}
              </div>
              {variants.length > 0 && (
                <div className="p-4 border-t border-stone-200 bg-white">
                  <p className="text-xs uppercase tracking-wider font-semibold text-stone-600 mb-3">Elige tu favorita ({variants.length} variantes)</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {variants.map((v, i) => (
                      <button
                        key={v.storage_path}
                        onClick={() => selectVariant(v)}
                        disabled={selectedVariant !== null}
                        data-testid={`ig-visual-variant-thumb-${i}`}
                        className={`relative border-2 ${selectedVariant === v.storage_path ? "border-[#E07A5F] ring-2 ring-[#E07A5F]/40" : "border-stone-200 hover:border-[#E07A5F]/60"} bg-stone-100 aspect-square overflow-hidden cursor-pointer disabled:cursor-wait`}
                      >
                        <img src={fileUrl(v.storage_path)} alt={`variant-${i + 1}`} className="w-full h-full object-cover" />
                        <span className="absolute top-1 left-1 bg-stone-900/80 text-white text-[10px] px-1.5 py-0.5">#{i + 1}</span>
                        {selectedVariant === v.storage_path && (
                          <div className="absolute inset-0 bg-[#E07A5F]/30 flex items-center justify-center">
                            <div className="animate-spin w-6 h-6 border-2 border-white border-t-transparent rounded-full" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-stone-500 italic mt-2">Click en una variante para seleccionarla y guardarla como archivo principal.</p>
                </div>
              )}
            </div>

            {/* Sora section */}
            <div className="border border-stone-200 bg-stone-50">
              <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-white flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Film className="w-5 h-5 text-[#3D5A80]" />
                  <h4 className="font-semibold text-sm">Sora 2 — Video 9:16</h4>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    onClick={() => copyPrompt("sora", data.prompt_sora)}
                    data-testid="ig-visual-copy-sora"
                    className="text-xs uppercase tracking-wider text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
                  >
                    <Copy className="w-3.5 h-3.5" /> {copied === "sora" ? "Copiado" : "Copiar"}
                  </button>
                  <select value={videoDuration} onChange={(e) => setVideoDuration(parseInt(e.target.value))} data-testid="ig-visual-video-duration" className="border border-stone-300 px-2 py-1 text-[11px] uppercase tracking-wider">
                    <option value={4}>4s</option>
                    <option value={8}>8s</option>
                    <option value={12}>12s</option>
                  </select>
                  <select value={videoModel} onChange={(e) => setVideoModel(e.target.value)} data-testid="ig-visual-video-model" className="border border-stone-300 px-2 py-1 text-[11px] uppercase tracking-wider">
                    <option value="sora-2">sora-2</option>
                    <option value="sora-2-pro">sora-2-pro</option>
                  </select>
                  <Button
                    onClick={generateVideoNow}
                    disabled={genVideoLoading}
                    data-testid="ig-visual-gen-video-btn"
                    className="bg-[#3D5A80] hover:bg-[#2C4159] text-white rounded-none text-xs"
                  >
                    {genVideoLoading
                      ? `Generando... ${Math.floor(videoElapsed / 60)}:${String(videoElapsed % 60).padStart(2, "0")}`
                      : videoStatus === "failed"
                      ? "Reintentar video"
                      : "Crear video ahora"}
                  </Button>
                </div>
              </div>
              {genVideoLoading && (
                <div className="px-4 py-3 border-b border-stone-200 bg-[#3D5A80]/5 flex items-center gap-3" data-testid="ig-visual-video-progress">
                  <div className="animate-spin w-4 h-4 border-2 border-[#3D5A80] border-t-transparent rounded-full" />
                  <div className="flex-1 text-[11px] text-stone-700 leading-relaxed">
                    <strong>Sora está generando tu video…</strong> Esto tarda entre 2 y 5 minutos.
                    <span className="text-stone-500"> Puedes cerrar este modal y seguir trabajando — el video se guardará automáticamente cuando esté listo.</span>
                  </div>
                  <span className="font-mono text-[11px] text-[#3D5A80] tabular-nums" data-testid="ig-visual-video-elapsed">
                    {Math.floor(videoElapsed / 60)}:{String(videoElapsed % 60).padStart(2, "0")}
                  </span>
                </div>
              )}
              {videoStatus === "failed" && !genVideoLoading && (
                <div className="px-4 py-2 border-b border-stone-200 bg-red-50 text-[11px] text-red-700 leading-relaxed" data-testid="ig-visual-video-failed">
                  <strong>La generación anterior falló.</strong>{item?.video_generation_error ? ` ${item.video_generation_error}` : ""} Ajusta el prompt o modelo y presiona <strong>Reintentar video</strong>.
                </div>
              )}
              {data?.scene_count >= 2 && videoDuration / data.scene_count < 2 && !genVideoLoading && (
                <div className="px-4 py-2 border-b border-stone-200 bg-orange-50 text-[11px] text-orange-800 leading-relaxed" data-testid="ig-visual-scene-warning">
                  <strong>⚠️ El hook enumera {data.scene_count} elementos ({data.enum_match})</strong> pero {videoDuration}s sólo da <strong>{(videoDuration / data.scene_count).toFixed(1)}s por escena</strong> — muy corto.
                  <div className="mt-1 text-stone-600">Recomendaciones:</div>
                  <ul className="list-disc pl-4 mt-0.5 text-stone-600 space-y-0.5">
                    <li>Sube la duración a <button type="button" onClick={() => setVideoDuration(12)} className="underline hover:text-orange-900">12s</button> ({(12 / data.scene_count).toFixed(1)}s por escena)</li>
                    <li>Cambia el tipo del post a <strong>carrusel</strong> ({data.scene_count} slides es ideal para enumeraciones)</li>
                    <li>Reformula el hook a 2-3 elementos (cambia &quot;5 razones&quot; por &quot;3 secretos&quot;)</li>
                  </ul>
                </div>
              )}
              {data?.scene_count >= 2 && videoDuration / data.scene_count >= 2 && !genVideoLoading && (
                <div className="px-4 py-2 border-b border-stone-200 bg-emerald-50 text-[11px] text-emerald-800 leading-relaxed" data-testid="ig-visual-scene-ok">
                  Sora estructurará <strong>{data.scene_count} escenas</strong> con hard-cuts (~{(videoDuration / data.scene_count).toFixed(1)}s c/u) para respetar el &quot;{data.enum_match}&quot; del hook.
                </div>
              )}
              {videoModel === "sora-2" && ["reel", "story"].includes(item?.ig_content_type) && !genVideoLoading && (
                <div className="px-4 py-2 border-b border-stone-200 bg-amber-50 text-[11px] text-amber-800" data-testid="ig-visual-sora-vertical-hint">
                  <strong>Nota:</strong> sora-2 sólo genera horizontal 16:9 (HD). Para 9:16 vertical (Reels/Stories) usa <strong>sora-2-pro</strong>.
                </div>
              )}
              {item?.ig_content_type === "reel" && /carrusel|carousel|slides?/i.test(item?.guion || "") && !genVideoLoading && (
                <div className="px-4 py-2 border-b border-stone-200 bg-blue-50 text-[11px] text-blue-800 leading-relaxed" data-testid="ig-visual-carousel-hint">
                  <strong>Nota de contenido:</strong> Tu guion menciona un <strong>carrusel</strong> pero el tipo del post es <strong>reel</strong>. Considera cambiar el tipo a <em>carrusel</em> y generar imágenes individuales con Nano Banana (4 variantes) — funcionará mejor que un video corto.
                </div>
              )}
              <div className="p-4 max-h-56 overflow-y-auto text-xs text-stone-700 font-mono whitespace-pre-wrap leading-relaxed">
                {data.prompt_sora}
              </div>

              {/* Brand text overlay controls */}
              <div className="border-t border-stone-200 bg-white p-4 space-y-3" data-testid="ig-visual-overlay-controls">
                <div className="flex items-center justify-between">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overlayEnabled}
                      onChange={(e) => setOverlayEnabled(e.target.checked)}
                      data-testid="ig-visual-overlay-toggle"
                      className="w-4 h-4 accent-[#3D5A80]"
                    />
                    <span className="text-xs uppercase tracking-wider font-semibold text-stone-700">Superponer hook con fuente de marca</span>
                  </label>
                  <span className="text-[10px] text-stone-400 italic">Sora no escribe texto legible — lo agregamos aquí</span>
                </div>
                {overlayEnabled && (
                  <div className="space-y-2 pl-6">
                    <input
                      type="text"
                      value={overlayHook}
                      onChange={(e) => setOverlayHook(e.target.value)}
                      placeholder="Texto a superponer (usa \n para salto de línea)"
                      maxLength={80}
                      data-testid="ig-visual-overlay-hook"
                      className="w-full border border-stone-300 px-2 py-1.5 text-xs font-serif-display"
                    />
                    <div className="flex flex-wrap gap-3 text-[11px]">
                      <div className="flex items-center gap-1">
                        <span className="uppercase tracking-wider text-stone-500">Fuente:</span>
                        <div className="flex gap-0 border border-stone-300">
                          <button type="button" onClick={() => setOverlayFont("serif")} data-testid="ig-visual-overlay-font-serif" className={`px-2 py-0.5 ${overlayFont === "serif" ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`} title="Cormorant Garamond">Serif</button>
                          <button type="button" onClick={() => setOverlayFont("sans")} data-testid="ig-visual-overlay-font-sans" className={`px-2 py-0.5 ${overlayFont === "sans" ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`} title="Outfit">Sans</button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="uppercase tracking-wider text-stone-500">Posición:</span>
                        <select value={overlayPosition} onChange={(e) => setOverlayPosition(e.target.value)} data-testid="ig-visual-overlay-position" className="border border-stone-300 px-1.5 py-0.5 text-[11px]">
                          <option value="top">Arriba</option>
                          <option value="center">Centro</option>
                          <option value="bottom-safe">Abajo (safe IG)</option>
                          <option value="bottom">Abajo</option>
                        </select>
                      </div>
                    </div>
                    <p className="text-[10px] text-stone-500 italic">Se usa <strong>{overlayFont === "serif" ? "Cormorant Garamond" : "Outfit"}</strong> (fuente oficial de Villas Pura Vida) con un gradiente sutil detrás para legibilidad.</p>
                  </div>
                )}
              </div>

              <div className="px-4 py-2 border-t border-stone-200 text-[11px] text-stone-500 italic">
                Click <strong>Crear video ahora</strong> para generar directo con Sora 2 (2-5 min). O copia el prompt para usarlo en Sora web, Runway, Kling.
              </div>
            </div>

            {/* Cinematography notes */}
            <div className="border border-[#E07A5F]/30 bg-[#E07A5F]/5 p-4">
              <p className="text-xs uppercase tracking-wider font-semibold text-[#E07A5F] mb-2">Notas de dirección (para grabar con humanos)</p>
              <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{data.cinematography_notes_es}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ---------- Text Overlay Dialog (add text layer to an existing video) ----------
function TextOverlayDialog({ item, token, defaultText, onClose, onApplied }) {
  const [text, setText] = useState(defaultText || "");
  const [position, setPosition] = useState("bottom-safe");
  const [font, setFont] = useState("serif");
  const [gradient, setGradient] = useState(true);
  const [applying, setApplying] = useState(false);

  const apply = async () => {
    if (!text.trim()) { toast.error("Escribe el texto que quieres superponer"); return; }
    setApplying(true);
    try {
      const { data } = await api.post(
        `/admin/ig-media/${item.id}/overlay-text`,
        { text: text.trim(), position, font, show_gradient: gradient },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }
      );
      toast.success("Capa de texto aplicada al video");
      onApplied(data.item);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aplicar la capa de texto");
    } finally { setApplying(false); }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/80 z-[125] flex items-center justify-center p-4" data-testid="ig-text-overlay-dialog" onClick={onClose}>
      <div className="bg-white max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-stone-200 px-5 py-3">
          <h3 className="font-serif-display text-xl">Añadir capa de texto al video</h3>
          <button onClick={onClose} data-testid="ig-text-overlay-close" className="text-stone-500 hover:text-stone-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1.5">Texto</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Escribe el texto (Enter para nueva línea)"
              maxLength={120}
              rows={2}
              data-testid="ig-text-overlay-text"
              className="w-full border border-stone-300 px-3 py-2 text-sm font-serif-display leading-tight resize-none focus:outline-none focus:border-[#3D5A80]"
            />
            <p className="text-[10px] text-stone-400 mt-1">Máx 120 caracteres · el texto se ajusta automáticamente en 1-3 líneas</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1.5">Fuente</label>
              <div className="flex gap-0 border border-stone-300">
                <button type="button" onClick={() => setFont("serif")} data-testid="ig-text-overlay-font-serif" className={`flex-1 py-1.5 text-xs uppercase tracking-wider ${font === "serif" ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`} title="Cormorant Garamond">Serif</button>
                <button type="button" onClick={() => setFont("sans")} data-testid="ig-text-overlay-font-sans" className={`flex-1 py-1.5 text-xs uppercase tracking-wider ${font === "sans" ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`} title="Outfit">Sans</button>
              </div>
              <p className="text-[10px] text-stone-400 mt-1 italic">{font === "serif" ? "Cormorant Garamond (títulos)" : "Outfit (cuerpo)"}</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1.5">Posición</label>
              <select value={position} onChange={(e) => setPosition(e.target.value)} data-testid="ig-text-overlay-position" className="w-full border border-stone-300 px-2 py-1.5 text-xs">
                <option value="top">Arriba</option>
                <option value="center">Centro</option>
                <option value="bottom-safe">Abajo (safe IG)</option>
                <option value="bottom">Abajo</option>
              </select>
            </div>
          </div>

          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={gradient} onChange={(e) => setGradient(e.target.checked)} data-testid="ig-text-overlay-gradient" className="w-4 h-4 accent-[#3D5A80]" />
            <span className="text-xs text-stone-700">Añadir gradiente sutil detrás (mejora legibilidad)</span>
          </label>

          <div className="bg-stone-50 border border-stone-200 p-3 text-[11px] text-stone-500 leading-relaxed">
            <strong>💡 Tip:</strong> Se genera un nuevo video con la capa aplicada (el original en el bucket permanece, pero se reemplaza el archivo principal del post). Toma 5-15s dependiendo de la duración del video.
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3">
          <button onClick={onClose} type="button" data-testid="ig-text-overlay-cancel" className="text-xs uppercase tracking-wider text-stone-600 hover:text-stone-900 px-3 py-2">Cancelar</button>
          <Button onClick={apply} disabled={applying || !text.trim()} data-testid="ig-text-overlay-apply" className="bg-[#3D5A80] hover:bg-[#2C4159] text-white rounded-none text-xs">
            {applying ? "Aplicando..." : "Aplicar al video"}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ---------- Asset preview lightbox ----------
function AssetPreview({ asset, onClose }) {
  if (!asset) return null;
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-stone-900/90 z-[120] flex items-center justify-center p-4"
      data-testid="ig-asset-preview"
    >
      <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white z-10" data-testid="ig-asset-preview-close">
        <X className="w-7 h-7" />
      </button>
      <div onClick={(e) => e.stopPropagation()} className="max-w-[95vw] max-h-[95vh] flex flex-col items-center gap-3">
        {asset.asset_type === "video" ? (
          <video
            src={fileUrl(asset.storage_path)}
            controls
            autoPlay
            className="max-w-[95vw] max-h-[85vh] object-contain bg-black"
          />
        ) : (
          <img
            src={fileUrl(asset.storage_path)}
            alt={asset.original_filename}
            className="max-w-[95vw] max-h-[85vh] object-contain"
          />
        )}
        <div className="text-white text-center">
          <p className="text-sm font-medium">{asset.original_filename}</p>
          {(asset.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 justify-center mt-2">
              {asset.tags.map((t) => (
                <span key={t} className="text-[11px] bg-white/10 text-white/90 px-2 py-0.5">{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Asset thumbnail (used in library + picker) ----------
function AssetThumb({ asset, onDelete, onEditTags, onPreview, selectable, selected, onToggle }) {
  const handleMediaClick = (e) => {
    e.stopPropagation();
    if (selectable) {
      onToggle && onToggle(asset);
    } else {
      onPreview && onPreview(asset);
    }
  };
  return (
    <div className={`group relative border ${selected ? "border-[#E07A5F] ring-2 ring-[#E07A5F]/40" : "border-stone-200"} bg-white overflow-hidden`}>
      {selectable && (
        <button onClick={() => onToggle(asset)} className={`absolute top-2 left-2 z-10 w-6 h-6 flex items-center justify-center border-2 ${selected ? "bg-[#E07A5F] border-[#E07A5F] text-white" : "bg-white border-stone-300"}`}>
          {selected && <Check className="w-4 h-4" />}
        </button>
      )}
      <button
        type="button"
        onClick={handleMediaClick}
        className="block w-full aspect-square bg-stone-100 cursor-zoom-in relative"
        data-testid={`ig-thumb-${asset.id}`}
        aria-label={selectable ? "Seleccionar" : "Ver a tamaño completo"}
      >
        {asset.asset_type === "video" ? (
          <video src={fileUrl(asset.storage_path)} className="w-full h-full object-cover pointer-events-none" muted preload="metadata" />
        ) : (
          <img src={fileUrl(asset.storage_path)} alt={asset.original_filename} className="w-full h-full object-cover pointer-events-none" />
        )}
        {asset.asset_type === "video" && (
          <span className="absolute top-2 right-2 bg-stone-900/80 text-white text-[10px] px-1.5 py-0.5 uppercase tracking-wider flex items-center gap-1">
            <Video className="w-3 h-3" /> Video
          </span>
        )}
      </button>
      <div className="p-2 space-y-1">
        <p className="text-[11px] text-stone-500 truncate" title={asset.original_filename}>{asset.original_filename}</p>
        <div className="flex flex-wrap gap-1">
          {(asset.tags || []).slice(0, 5).map((t) => (
            <span key={t} className="text-[10px] bg-stone-100 text-stone-600 px-1.5 py-0.5">{t}</span>
          ))}
          {(asset.tags || []).length > 5 && <span className="text-[10px] text-stone-400">+{(asset.tags || []).length - 5}</span>}
        </div>
        {!selectable && (
          <div className="flex justify-end gap-1 pt-1">
            <button onClick={() => onEditTags(asset)} className="text-stone-400 hover:text-[#E07A5F]" title="Editar tags"><TagIcon className="w-3.5 h-3.5" /></button>
            <button onClick={() => onDelete(asset)} className="text-stone-400 hover:text-red-600" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Tags editor popover ----------
function TagsEditor({ asset, taxonomy, token, onClose, onSaved }) {
  const [selected, setSelected] = useState(new Set(asset.tags || []));
  const [notes, setNotes] = useState(asset.notes || "");
  const [saving, setSaving] = useState(false);

  const toggle = (tag) => {
    const n = new Set(selected);
    if (n.has(tag)) n.delete(tag); else n.add(tag);
    setSelected(n);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/ig-assets/${asset.id}`, { tags: Array.from(selected), notes }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved(data);
      toast.success("Tags actualizadas");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/70 z-[110] flex items-center justify-center p-4 overflow-y-auto" data-testid="tags-editor-modal">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4 sticky top-0 bg-white">
          <h3 className="font-serif-display text-xl">Etiquetas · {asset.original_filename}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          {Object.entries(taxonomy || {}).map(([cat, tags]) => (
            <div key={cat}>
              <p className="text-xs uppercase tracking-wider text-stone-500 mb-2 font-semibold">{cat}</p>
              <div className="flex flex-wrap gap-1.5">
                {tags.map((t) => {
                  const isSel = selected.has(t);
                  return (
                    <button
                      key={t}
                      onClick={() => toggle(t)}
                      data-testid={`tag-${t}`}
                      className={`text-xs px-2.5 py-1 border ${isSel ? "bg-[#E07A5F] border-[#E07A5F] text-white" : "bg-white border-stone-200 text-stone-700 hover:border-stone-400"}`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <div>
            <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Notas (opcional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full border border-stone-300 px-3 py-2 text-sm rounded-none" />
          </div>
        </div>
        <div className="border-t border-stone-200 px-6 py-3 flex justify-end gap-3 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} className="rounded-none">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="tags-editor-save" className="bg-[#E07A5F] hover:bg-[#D3674B] text-white rounded-none px-6">{saving ? "Guardando..." : "Guardar"}</Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Library view (raw assets) ----------
function LibraryView({ token, taxonomy, onPickForItem, pickerMode = false, initialSelection = [], onConfirmSelection, onCancel }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tagFilter, setTagFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [manualTags, setManualTags] = useState("");
  const [editingTags, setEditingTags] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [selected, setSelected] = useState(new Set(initialSelection));
  const [layout, setLayout] = useState("grid"); // grid | kanban

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tagFilter) params.set("tag", tagFilter);
      if (typeFilter) params.set("asset_type", typeFilter);
      const { data } = await api.get(`/admin/ig-assets?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      setAssets(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al cargar"); }
    finally { setLoading(false); }
  }, [tagFilter, typeFilter, token]);

  useEffect(() => { load(); }, [load]);

  const bulkUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      if (manualTags.trim()) fd.append("manual_tags", manualTags.trim());
      const { data } = await api.post("/admin/ig-assets/upload", fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.count} archivo(s) subidos`);
      if (data.errors?.length) toast.warning(`${data.errors.length} con errores`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al subir"); }
    finally { setUploading(false); }
  };

  const deleteAsset = async (a) => {
    if (!window.confirm(`¿Borrar "${a.original_filename}"?`)) return;
    try {
      await api.delete(`/admin/ig-assets/${a.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const toggleSelect = (a) => {
    const n = new Set(selected);
    if (n.has(a.id)) n.delete(a.id); else n.add(a.id);
    setSelected(n);
  };

  const allTagOptions = useMemo(() => {
    const s = new Set();
    for (const cat of Object.values(taxonomy || {})) for (const t of cat) s.add(t);
    return Array.from(s).sort();
  }, [taxonomy]);

  return (
    <div className="space-y-4" data-testid="ig-library-view">
      {!pickerMode && (
        <div className="border-2 border-dashed border-stone-300 bg-white p-6">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="flex-1">
              <p className="text-sm text-stone-700 font-semibold">Sube fotos y videos en lote</p>
              <p className="text-xs text-stone-500">Yo detecto tags automáticamente del nombre del archivo (ej. <code className="bg-stone-100 px-1">IMG_playa-sola_amanecer.jpg</code>). También puedes agregar tags manuales que se aplican a todos.</p>
              <input
                type="text"
                value={manualTags}
                onChange={(e) => setManualTags(e.target.value)}
                data-testid="ig-library-manual-tags"
                placeholder="Tags manuales separados por coma (ej: casita, atardecer)"
                className="mt-2 w-full border border-stone-300 px-3 py-2 text-xs rounded-none font-mono"
              />
            </div>
            <label className="block">
              <input
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                className="hidden"
                onChange={(e) => { bulkUpload(e.target.files); e.target.value = ""; }}
                disabled={uploading}
                data-testid="ig-library-upload"
              />
              <span className={`cursor-pointer inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-700 text-white px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                <Upload className="w-4 h-4" /> {uploading ? "Subiendo..." : "Subir varios"}
              </span>
            </label>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        {!pickerMode && (
          <div className="flex gap-1 mr-2">
            <button onClick={() => setLayout("grid")} data-testid="ig-library-layout-grid" className={`px-3 py-1.5 text-xs uppercase tracking-wider ${layout === "grid" ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
              Grid
            </button>
            <button onClick={() => setLayout("kanban")} data-testid="ig-library-layout-kanban" className={`px-3 py-1.5 text-xs uppercase tracking-wider ${layout === "kanban" ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
              Kanban
            </button>
          </div>
        )}
        <Filter className="w-4 h-4 text-stone-400" />
        <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} data-testid="ig-library-filter-tag" className="border border-stone-300 px-2 py-1.5 text-xs uppercase tracking-wider">
          <option value="">Todas las tags</option>
          {allTagOptions.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="ig-library-filter-type" className="border border-stone-300 px-2 py-1.5 text-xs uppercase tracking-wider">
          <option value="">Foto y video</option>
          <option value="photo">Solo fotos</option>
          <option value="video">Solo videos</option>
        </select>
        <span className="ml-auto text-xs text-stone-500">
          {assets.length} assets{pickerMode && ` · ${selected.size} seleccionados`}
        </span>
      </div>

      {loading ? (
        <p className="text-center text-stone-400 py-16 text-sm">Cargando...</p>
      ) : assets.length === 0 ? (
        <p className="text-center text-stone-400 py-16 text-sm">Aún no hay assets. Sube tus primeras fotos/videos arriba.</p>
      ) : layout === "kanban" && !pickerMode ? (
        <KanbanView
          assets={assets}
          taxonomy={taxonomy}
          token={token}
          onUpdate={(updated) => setAssets((prev) => prev.map((a) => a.id === updated.id ? updated : a))}
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {assets.map((a) => (
            <AssetThumb
              key={a.id}
              asset={a}
              onDelete={deleteAsset}
              onEditTags={setEditingTags}
              onPreview={setPreviewing}
              selectable={pickerMode}
              selected={selected.has(a.id)}
              onToggle={toggleSelect}
            />
          ))}
        </div>
      )}

      {pickerMode && (
        <div className="border-t border-stone-200 pt-4 flex justify-end gap-3 sticky bottom-0 bg-white pb-2">
          <Button variant="outline" onClick={onCancel} className="rounded-none">Cancelar</Button>
          <Button onClick={() => onConfirmSelection(Array.from(selected))} data-testid="ig-picker-confirm" className="bg-[#E07A5F] hover:bg-[#D3674B] text-white rounded-none">
            Adjuntar {selected.size} asset(s)
          </Button>
        </div>
      )}

      {editingTags && (
        <TagsEditor
          asset={editingTags}
          taxonomy={taxonomy}
          token={token}
          onClose={() => setEditingTags(null)}
          onSaved={(u) => { setEditingTags(null); setAssets((prev) => prev.map((a) => a.id === u.id ? u : a)); }}
        />
      )}
      {previewing && <AssetPreview asset={previewing} onClose={() => setPreviewing(null)} />}
    </div>
  );
}

// ---------- Kanban view (drag between tag columns) ----------
function KanbanView({ assets, taxonomy, token, onUpdate }) {
  const [category, setCategory] = useState("escenario");
  const [dragOver, setDragOver] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  const columns = useMemo(() => {
    const tagsInCat = taxonomy?.[category] || [];
    const cols = tagsInCat.map((tag) => ({
      tag,
      items: assets.filter((a) => (a.tags || []).includes(tag)),
    }));
    // "Sin categoría" bucket: assets that have NO tag from this category
    const catSet = new Set(tagsInCat);
    const uncategorized = assets.filter((a) => !(a.tags || []).some((t) => catSet.has(t)));
    cols.push({ tag: "__none__", items: uncategorized });
    return cols;
  }, [assets, taxonomy, category]);

  const onDragStart = (e, asset, sourceTag) => {
    e.dataTransfer.setData("application/json", JSON.stringify({ id: asset.id, sourceTag }));
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = async (e, targetTag) => {
    e.preventDefault();
    setDragOver(null);
    try {
      const payload = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
      const asset = assets.find((a) => a.id === payload.id);
      if (!asset) return;
      if (payload.sourceTag === targetTag) return;
      let newTags = (asset.tags || []).filter((t) => t !== payload.sourceTag);
      if (targetTag !== "__none__" && !newTags.includes(targetTag)) newTags.push(targetTag);
      // Save
      const { data } = await api.put(`/admin/ig-assets/${asset.id}`, { tags: newTags }, { headers: { Authorization: `Bearer ${token}` } });
      onUpdate(data);
      toast.success(
        targetTag === "__none__"
          ? `Sacado de "${payload.sourceTag}"`
          : `Movido a "${targetTag}"`
      );
    } catch (err) {
      toast.error("Error al mover");
    }
  };

  const catLabel = { escenario: "Escenario", sujeto: "Sujeto", momento: "Momento del día", detalle: "Detalle poético", pilar: "Pilar (1-7)", propiedad: "Propiedad", toma: "Tipo de toma" };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <label className="text-xs uppercase tracking-wider text-stone-500">Ver columnas por:</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="ig-kanban-category" className="border border-stone-300 px-3 py-1.5 text-xs uppercase tracking-wider">
          {Object.keys(taxonomy || {}).map((c) => (
            <option key={c} value={c}>{catLabel[c] || c}</option>
          ))}
        </select>
        <p className="text-xs text-stone-400 italic">Arrastra un asset a otra columna para cambiar sus tags de esta categoría.</p>
      </div>
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3" style={{ minWidth: `${columns.length * 220}px` }}>
          {columns.map((col) => (
            <div
              key={col.tag}
              data-testid={`ig-kanban-col-${col.tag}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(col.tag); }}
              onDragLeave={() => setDragOver((prev) => prev === col.tag ? null : prev)}
              onDrop={(e) => onDrop(e, col.tag)}
              className={`w-52 flex-shrink-0 border ${dragOver === col.tag ? "border-[#E07A5F] bg-[#E07A5F]/5" : "border-stone-200 bg-stone-50"} p-2`}
            >
              <div className="flex items-center justify-between mb-2 px-1">
                <p className={`text-xs uppercase tracking-wider font-semibold ${col.tag === "__none__" ? "text-stone-400 italic" : "text-stone-700"}`}>
                  {col.tag === "__none__" ? "Sin categoría" : col.tag}
                </p>
                <span className="text-[10px] text-stone-400">{col.items.length}</span>
              </div>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {col.items.length === 0 && (
                  <p className="text-[11px] text-stone-400 italic py-4 text-center">Vacío</p>
                )}
                {col.items.map((a) => (
                  <div
                    key={`${col.tag}-${a.id}`}
                    draggable
                    onDragStart={(e) => onDragStart(e, a, col.tag)}
                    onClick={() => setPreviewing(a)}
                    data-testid={`ig-kanban-card-${a.id}`}
                    className="bg-white border border-stone-200 cursor-move hover:shadow-md hover:ring-1 hover:ring-[#E07A5F]/40 transition-shadow"
                    title="Arrastra a otra columna · Click para ver grande"
                  >
                    <div className="aspect-square bg-stone-100">
                      {a.asset_type === "video" ? (
                        <video src={fileUrl(a.storage_path)} className="w-full h-full object-cover pointer-events-none" muted preload="metadata" />
                      ) : (
                        <img src={fileUrl(a.storage_path)} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
                      )}
                    </div>
                    <p className="text-[10px] text-stone-500 px-1.5 py-1 truncate" title={a.original_filename}>{a.original_filename}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {previewing && <AssetPreview asset={previewing} onClose={() => setPreviewing(null)} />}
    </div>
  );
}



// ---------- Asset picker modal (wraps LibraryView in pickerMode) ----------
function AssetPickerModal({ token, taxonomy, initialSelection, onConfirm, onClose }) {
  return (
    <div className="fixed inset-0 bg-stone-900/70 z-[105] flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white max-w-6xl w-full my-4 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4 sticky top-0 bg-white z-10">
          <h3 className="font-serif-display text-2xl">Adjuntar assets al post</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">
          <LibraryView
            token={token}
            taxonomy={taxonomy}
            pickerMode={true}
            initialSelection={initialSelection}
            onConfirmSelection={onConfirm}
            onCancel={onClose}
          />
        </div>
      </div>
    </div>
  );
}


// ---------- Main manager ----------
export default function IGMediaManager({ token }) {
  const [tab, setTab] = useState("posts"); // posts | library
  const [items, setItems] = useState([]);
  const [view, setView] = useState("list"); // list | calendar
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ estado: "", pilar: "", property_id: "", ig_content_type: "" });
  const [month, setMonth] = useState(currentMonth());
  const [editing, setEditing] = useState(null);
  const [previewingPost, setPreviewingPost] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [airbnbState, setAirbnbState] = useState(null);
  const [seoState, setSeoState] = useState(null);
  const [taxonomy, setTaxonomy] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/ig-assets/taxonomy", { headers: { Authorization: `Bearer ${token}` } });
        setTaxonomy(data || {});
      } catch { /* silent */ }
    })();
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.estado) params.set("estado", filter.estado);
      if (filter.pilar) params.set("pilar", filter.pilar);
      if (filter.property_id) params.set("property_id", filter.property_id);
      if (filter.ig_content_type) params.set("ig_content_type", filter.ig_content_type);
      if (view === "calendar") params.set("month", month);
      const { data } = await api.get(`/admin/ig-media?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar");
    } finally { setLoading(false); }
  }, [filter, view, month, token]);

  useEffect(() => { load(); }, [load]);

  // Background poll: if any item has a pending Sora video generation, refresh the list every 20s
  useEffect(() => {
    const hasPending = items.some((it) => it.video_generation_status === "pending");
    if (!hasPending) return;
    const t = setInterval(() => { load(); }, 20000);
    return () => clearInterval(t);
  }, [items, load]);

  const handleDelete = async (it) => {
    if (!window.confirm(`¿Borrar "${it.hook}"?`)) return;
    try {
      await api.delete(`/admin/ig-media/${it.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al eliminar"); }
  };

  const handleReschedule = async (itemId, newDate) => {
    // Optimistic update
    setItems((prev) => prev.map((it) => it.id === itemId ? { ...it, fecha_planeada: newDate } : it));
    try {
      await api.put(`/admin/ig-media/${itemId}`, { fecha_planeada: newDate }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Reprogramado a ${newDate}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al reprogramar");
      load(); // rollback via reload
    }
  };

  const handleQuickStatus = async (it, nxt) => {
    const info = estadoInfo(nxt);
    setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, estado: nxt } : p));
    try {
      await api.put(`/admin/ig-media/${it.id}`, { estado: nxt }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`"${(it.hook || "").slice(0, 40)}" → ${info.label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar estado");
      load();
    }
  };

  const seedMes1 = async () => {
    const startDate = window.prompt("Fecha de inicio del plan Mes 1 (YYYY-MM-DD) — dejar vacío para hoy:", todayISO());
    if (startDate === null) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/ig-media/seed-mes1", { start_date: startDate || null, replace_existing_seed: false }, { headers: { Authorization: `Bearer ${token}` } });
      if (data.skipped) toast.info(data.message);
      else toast.success(`${data.seeded} items del plan Mes 1 pre-cargados como ideas`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al hacer seed"); }
    finally { setSeeding(false); }
  };

  return (
    <div className="space-y-6" data-testid="ig-media-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-serif-display text-2xl">Galería Instagram</h3>
          <p className="text-sm text-stone-500 mt-1">Fotos, videos, hooks, guiones y calendario editorial · estrategia &quot;Playa Secreta&quot;</p>
        </div>
        <div className="flex gap-2">
          {tab === "posts" && (
            <>
              <Button
                onClick={async () => {
                  try {
                    toast.info("Generando PDF de estrategia…");
                    const res = await api.get("/admin/ig-media/estrategia.pdf", {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: "blob",
                    });
                    const blob = new Blob([res.data], { type: "application/pdf" });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "estrategia-instagram-playa-secreta.pdf";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Estrategia PDF descargada");
                  } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "No se pudo generar el PDF";
                    toast.error(typeof msg === "string" ? msg : "No se pudo generar el PDF");
                  }
                }}
                data-testid="ig-download-estrategia-btn"
                variant="outline"
                className="border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white rounded-none"
              >
                <Download className="w-4 h-4 mr-2" /> Estrategia (PDF)
              </Button>
              <Button
                onClick={async () => {
                  try {
                    toast.info("Generando PDF consolidado…");
                    const res = await api.get("/admin/ig-media/edit-briefs-all.pdf", {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: "blob",
                    });
                    const blob = new Blob([res.data], { type: "application/pdf" });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "guia-edicion-completa-posts-1-9.pdf";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("PDF consolidado descargado");
                  } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "No se pudo generar el PDF";
                    toast.error(typeof msg === "string" ? msg : "No se pudo generar el PDF");
                  }
                }}
                data-testid="ig-download-all-briefs-btn"
                variant="outline"
                className="border-[#3D5A80] text-[#3D5A80] hover:bg-[#3D5A80] hover:text-white rounded-none"
              >
                <Download className="w-4 h-4 mr-2" /> Guía completa (1-9)
              </Button>
              <Button
                onClick={async () => {
                  try {
                    toast.info("Descargando Prólogo…");
                    const res = await api.get("/admin/brand/prologo.pdf", {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: "blob",
                    });
                    const blob = new Blob([res.data], { type: "application/pdf" });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "Villas-Pura-Vida-Manual-de-Marca-Prologo.pdf";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Prólogo descargado");
                  } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "No se pudo descargar el Prólogo";
                    toast.error(typeof msg === "string" ? msg : "No se pudo descargar el Prólogo");
                  }
                }}
                data-testid="brand-download-prologo-btn"
                variant="outline"
                className="border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white rounded-none"
              >
                <Download className="w-4 h-4 mr-2" /> Prólogo de Marca
              </Button>
              <Button
                onClick={async () => {
                  try {
                    toast.info("Descargando Capítulo 1…");
                    const res = await api.get("/admin/brand/capitulo-1.pdf", {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: "blob",
                    });
                    const blob = new Blob([res.data], { type: "application/pdf" });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "Villas-Pura-Vida-Manual-de-Marca-Capitulo-1.pdf";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Capítulo 1 descargado");
                  } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "No se pudo descargar el Capítulo 1";
                    toast.error(typeof msg === "string" ? msg : "No se pudo descargar el Capítulo 1");
                  }
                }}
                data-testid="brand-download-cap1-btn"
                variant="outline"
                className="border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white rounded-none"
              >
                <Download className="w-4 h-4 mr-2" /> Capítulo 1
              </Button>
              <Button
                onClick={async () => {
                  try {
                    toast.info("Descargando Capítulo 2…");
                    const res = await api.get("/admin/brand/capitulo-2.pdf", {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: "blob",
                    });
                    const blob = new Blob([res.data], { type: "application/pdf" });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "Villas-Pura-Vida-Manual-de-Marca-Capitulo-2.pdf";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Capítulo 2 descargado");
                  } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "No se pudo descargar el Capítulo 2";
                    toast.error(typeof msg === "string" ? msg : "No se pudo descargar el Capítulo 2");
                  }
                }}
                data-testid="brand-download-cap2-btn"
                variant="outline"
                className="border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white rounded-none"
              >
                <Download className="w-4 h-4 mr-2" /> Capítulo 2
              </Button>
              <Button
                onClick={async () => {
                  try {
                    toast.info("Descargando Documento Cero…");
                    const res = await api.get("/admin/brand/documento-cero.pdf", {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: "blob",
                    });
                    const blob = new Blob([res.data], { type: "application/pdf" });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "Villas-Pura-Vida-Documento-Cero-Manual-Maestro.pdf";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Documento Cero descargado");
                  } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "No se pudo descargar el Documento Cero";
                    toast.error(typeof msg === "string" ? msg : "No se pudo descargar el Documento Cero");
                  }
                }}
                data-testid="brand-download-doc0-btn"
                variant="outline"
                className="border-amber-900 text-amber-900 hover:bg-amber-900 hover:text-white rounded-none"
              >
                <Download className="w-4 h-4 mr-2" /> Documento Cero
              </Button>
              <Button
                onClick={async () => {
                  try {
                    toast.info("Descargando Manual Airbnb Casita…");
                    const res = await api.get("/admin/brand/airbnb-casita.pdf", {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: "blob",
                    });
                    const blob = new Blob([res.data], { type: "application/pdf" });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "Villas-Pura-Vida-Manual-Airbnb-La-Casita.pdf";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Manual Airbnb Casita descargado");
                  } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "No se pudo descargar el Manual";
                    toast.error(typeof msg === "string" ? msg : "No se pudo descargar el Manual");
                  }
                }}
                data-testid="brand-download-airbnb-casita-btn"
                variant="outline"
                className="border-amber-900 text-amber-900 hover:bg-amber-900 hover:text-white rounded-none"
              >
                <Download className="w-4 h-4 mr-2" /> Airbnb · La Casita
              </Button>
              <Button
                onClick={async () => {
                  try {
                    toast.info("Descargando Manual Airbnb Loft…");
                    const res = await api.get("/admin/brand/airbnb-loft.pdf", {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: "blob",
                    });
                    const blob = new Blob([res.data], { type: "application/pdf" });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "Villas-Pura-Vida-Manual-Airbnb-El-Loft.pdf";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Manual Airbnb Loft descargado");
                  } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "No se pudo descargar el Manual";
                    toast.error(typeof msg === "string" ? msg : "No se pudo descargar el Manual");
                  }
                }}
                data-testid="brand-download-airbnb-loft-btn"
                variant="outline"
                className="border-amber-900 text-amber-900 hover:bg-amber-900 hover:text-white rounded-none"
              >
                <Download className="w-4 h-4 mr-2" /> Airbnb · El Loft
              </Button>
              <Button
                onClick={async () => {
                  try {
                    toast.info("Descargando tarjetas impresas (ZIP)…");
                    const res = await fetch(`${window.location.origin}/api/brand/house-cards.zip`);
                    if (!res.ok) throw new Error("No se pudo descargar el ZIP");
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "Villas-Pura-Vida-Tarjetas-Impresas-Carta.zip";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Tarjetas impresas descargadas (7 archivos tamaño Carta)");
                  } catch (e) {
                    const msg = e?.message || "No se pudo descargar el ZIP";
                    toast.error(msg);
                  }
                }}
                data-testid="brand-download-house-cards-btn"
                variant="outline"
                className="border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white rounded-none"
                title="Descarga 7 tarjetas tamaño Carta impresas (bienvenida, reglamento, climas, Instagram QR, etc.)"
              >
                <Download className="w-4 h-4 mr-2" /> Tarjetas impresas
              </Button>
              <Button
                onClick={async () => {
                  // Download 4 files in sequence: casita PNG, casita PDF, loft PNG, loft PDF
                  const items = [
                    { prop: "casita", kind: "png" },
                    { prop: "casita", kind: "pdf" },
                    { prop: "loft",   kind: "png" },
                    { prop: "loft",   kind: "pdf" },
                  ];
                  try {
                    for (const it of items) {
                      const res = await api.get(`/admin/brand/door-sticker/${it.prop}.${it.kind}`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: "blob",
                      });
                      const mime = it.kind === "pdf" ? "application/pdf" : "image/png";
                      const blob = new Blob([res.data], { type: mime });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `sticker-puerta-${it.prop}.${it.kind}`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      await new Promise((r) => setTimeout(r, 350)); // small stagger so the browser doesn't block downloads
                    }
                    toast.success("4 stickers de puerta descargados (Casita + Loft, PNG + PDF)");
                  } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "No se pudieron descargar los stickers";
                    toast.error(msg);
                  }
                }}
                data-testid="brand-download-door-stickers-btn"
                variant="outline"
                className="border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white rounded-none"
                title="Stickers de QR para pegar en la puerta de cada casa (Casita y Loft, PNG + PDF)"
              >
                <Download className="w-4 h-4 mr-2" /> Stickers de puerta
              </Button>
              <Button
                onClick={async () => {
                  const ok = window.confirm(
                    "Se descargarán 191 fotos frescas de Airbnb (Casita + Loft) y se reemplazarán en el sitio. Toma 5-10 minutos. ¿Continuar?"
                  );
                  if (!ok) return;
                  try {
                    const { data } = await api.post("/admin/airbnb-refresh", {}, { headers: { Authorization: `Bearer ${token}` } });
                    if (!data.ok && data.error === "already_running") {
                      toast.warning("Ya está corriendo — mira el progreso abajo");
                    } else {
                      toast.success("Refresh iniciado. Puedes cerrar esta ventana — sigue en segundo plano.");
                    }
                    // Start polling status
                    const poll = setInterval(async () => {
                      try {
                        const { data: st } = await api.get("/admin/airbnb-refresh/status", { headers: { Authorization: `Bearer ${token}` } });
                        setAirbnbState(st);
                        if (!st.running) {
                          clearInterval(poll);
                          if (st.last_summary) {
                            toast.success(`Fotos actualizadas: ${st.last_summary.processed}/${st.last_summary.total} (${st.last_summary.errors} errores)`);
                          }
                        }
                      } catch { /* keep polling */ }
                    }, 3000);
                  } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "No se pudo iniciar el refresh";
                    toast.error(msg);
                  }
                }}
                data-testid="admin-airbnb-refresh-btn"
                variant="outline"
                className="border-rose-700 text-rose-700 hover:bg-rose-700 hover:text-white rounded-none"
                title="Descarga las fotos frescas de los anuncios de Airbnb y las reemplaza en el sitio"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Refrescar fotos Airbnb
              </Button>
              <Button
                onClick={async () => {
                  const ok = window.confirm(
                    "Se aplicará el SEO (slug, alt, title) a TODAS las fotos activas de esta base de datos. " +
                    "Corre esto una vez en Producción después de un deploy. ¿Continuar?"
                  );
                  if (!ok) return;
                  try {
                    const { data } = await api.post("/admin/seo-refresh", {}, { headers: { Authorization: `Bearer ${token}` } });
                    if (!data.ok && data.error === "already_running") {
                      toast.warning("Ya está corriendo — mira el progreso abajo");
                    } else {
                      toast.success("Migración SEO iniciada. Sigue el progreso abajo.");
                    }
                    const poll = setInterval(async () => {
                      try {
                        const { data: st } = await api.get("/admin/seo-refresh/status", { headers: { Authorization: `Bearer ${token}` } });
                        setSeoState(st);
                        if (!st.running) {
                          clearInterval(poll);
                          if (st.last_summary) {
                            toast.success(`SEO aplicado: ${st.last_summary.processed}/${st.last_summary.total} fotos (${st.last_summary.errors} errores)`);
                          }
                        }
                      } catch { /* keep polling */ }
                    }, 2000);
                  } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "No se pudo iniciar la migración SEO";
                    toast.error(msg);
                  }
                }}
                data-testid="admin-seo-refresh-btn"
                variant="outline"
                className="border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white rounded-none"
                title="Aplica slug/alt/title SEO a todas las fotos activas. Necesario una vez en Producción después de deploy."
              >
                <TagIcon className="w-4 h-4 mr-2" /> Aplicar SEO a fotos
              </Button>
              <Button onClick={seedMes1} disabled={seeding} data-testid="ig-seed-btn" variant="outline" className="border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white rounded-none">
                <Sparkles className="w-4 h-4 mr-2" /> {seeding ? "Cargando..." : "Cargar plan Mes 1"}
              </Button>
              <Button onClick={() => setEditing({})} data-testid="ig-new-btn" className="bg-stone-900 hover:bg-stone-700 text-white rounded-none">
                <Plus className="w-4 h-4 mr-2" /> Nuevo item
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Airbnb refresh progress panel */}
      {airbnbState && (
        <div
          data-testid="airbnb-refresh-progress"
          className="border border-rose-200 bg-rose-50 px-4 py-3 text-sm flex items-center gap-3"
        >
          <Sparkles className={`w-4 h-4 ${airbnbState.running ? "animate-pulse text-rose-700" : "text-rose-500"}`} />
          <span className="text-rose-900">
            <strong>Refresh Airbnb:</strong>{" "}
            {airbnbState.running
              ? `${airbnbState.processed}/${airbnbState.total} fotos (${airbnbState.sections_done?.length || 0} secciones listas)`
              : airbnbState.last_summary
                ? `Completado — ${airbnbState.last_summary.processed}/${airbnbState.last_summary.total} fotos${airbnbState.last_summary.errors ? `, ${airbnbState.last_summary.errors} errores` : ""}`
                : "Sin datos"}
          </span>
          {airbnbState.total > 0 && (
            <div className="flex-1 h-2 bg-rose-100 max-w-md">
              <div
                className="h-full bg-rose-600 transition-all"
                style={{ width: `${Math.min(100, Math.round((airbnbState.processed / Math.max(1, airbnbState.total)) * 100))}%` }}
              />
            </div>
          )}
          {!airbnbState.running && (
            <button onClick={() => setAirbnbState(null)} className="text-rose-700 hover:text-rose-900 text-xs uppercase tracking-wider ml-auto">
              Cerrar
            </button>
          )}
        </div>
      )}

      {/* SEO refresh progress panel */}
      {seoState && (
        <div
          data-testid="seo-refresh-progress"
          className="border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm flex items-center gap-3"
        >
          <TagIcon className={`w-4 h-4 ${seoState.running ? "animate-pulse text-emerald-700" : "text-emerald-500"}`} />
          <span className="text-emerald-900">
            <strong>Migración SEO:</strong>{" "}
            {seoState.running
              ? `${seoState.processed}/${seoState.total} fotos (${seoState.buckets_done || 0}/${seoState.buckets_total || 0} secciones)`
              : seoState.last_summary
                ? `Completado — ${seoState.last_summary.processed}/${seoState.last_summary.total} fotos${seoState.last_summary.errors ? `, ${seoState.last_summary.errors} errores` : ""}`
                : "Sin datos"}
          </span>
          {seoState.total > 0 && (
            <div className="flex-1 h-2 bg-emerald-100 max-w-md">
              <div
                className="h-full bg-emerald-600 transition-all"
                style={{ width: `${Math.min(100, Math.round((seoState.processed / Math.max(1, seoState.total)) * 100))}%` }}
              />
            </div>
          )}
          {!seoState.running && (
            <button onClick={() => setSeoState(null)} className="text-emerald-700 hover:text-emerald-900 text-xs uppercase tracking-wider ml-auto">
              Cerrar
            </button>
          )}
        </div>
      )}

      {/* Sub-tab switcher */}
      <div className="flex gap-0 border-b border-stone-200">
        <button onClick={() => setTab("posts")} data-testid="ig-subtab-posts" className={`px-5 py-3 text-sm uppercase tracking-[0.15em] font-semibold border-b-2 transition-colors -mb-px inline-flex items-center gap-2 ${tab === "posts" ? "border-[#E07A5F] text-[#E07A5F]" : "border-transparent text-stone-500 hover:text-stone-900"}`}>
          <CalendarDays className="w-4 h-4" /> Posts / Calendario
        </button>
        <button onClick={() => setTab("library")} data-testid="ig-subtab-library" className={`px-5 py-3 text-sm uppercase tracking-[0.15em] font-semibold border-b-2 transition-colors -mb-px inline-flex items-center gap-2 ${tab === "library" ? "border-[#E07A5F] text-[#E07A5F]" : "border-transparent text-stone-500 hover:text-stone-900"}`}>
          <FolderOpen className="w-4 h-4" /> Biblioteca de assets
        </button>
      </div>

      {tab === "library" ? (
        <LibraryView token={token} taxonomy={taxonomy} />
      ) : (
      <>
      <div className="flex flex-wrap gap-2 items-center border-b border-stone-200 pb-4">
        <div className="flex gap-1 mr-4">
          <button onClick={() => setView("list")} data-testid="ig-view-list" className={`px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-2 ${view === "list" ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
            <ListIcon className="w-4 h-4" /> Lista
          </button>
          <button onClick={() => setView("calendar")} data-testid="ig-view-calendar" className={`px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-2 ${view === "calendar" ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
            <CalendarDays className="w-4 h-4" /> Calendario
          </button>
        </div>
        <Filter className="w-4 h-4 text-stone-400" />
        <select value={filter.estado} onChange={(e) => setFilter({ ...filter, estado: e.target.value })} data-testid="ig-filter-estado" className="border border-stone-300 px-2 py-1.5 text-xs uppercase tracking-wider">
          <option value="">Todos estados</option>
          {ESTADOS.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
        </select>
        <select value={filter.pilar} onChange={(e) => setFilter({ ...filter, pilar: e.target.value })} data-testid="ig-filter-pilar" className="border border-stone-300 px-2 py-1.5 text-xs uppercase tracking-wider">
          <option value="">Todos pilares</option>
          {PILARES.map((p) => <option key={p.id} value={p.id}>{p.short}</option>)}
        </select>
        <select value={filter.ig_content_type} onChange={(e) => setFilter({ ...filter, ig_content_type: e.target.value })} data-testid="ig-filter-type" className="border border-stone-300 px-2 py-1.5 text-xs uppercase tracking-wider">
          <option value="">Todos tipos</option>
          {CONTENT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select value={filter.property_id} onChange={(e) => setFilter({ ...filter, property_id: e.target.value })} data-testid="ig-filter-property" className="border border-stone-300 px-2 py-1.5 text-xs uppercase tracking-wider">
          <option value="">Todas propiedades</option>
          {PROPS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <span className="ml-auto text-xs text-stone-500">{items.length} items</span>
      </div>

      {loading ? (
        <p className="text-center text-stone-400 py-16 text-sm">Cargando...</p>
      ) : view === "list" ? (
        <ListView items={items} onEdit={setEditing} onDelete={handleDelete} onPreview={setPreviewingPost} onQuickStatus={handleQuickStatus} token={token} />
      ) : (
        <CalendarView items={items} month={month} setMonth={setMonth} onEdit={setEditing} onReschedule={handleReschedule} />
      )}
      </>
      )}

      {editing !== null && (
        <ItemEditor
          item={editing}
          token={token}
          taxonomy={taxonomy}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setEditing(updated);
            load();
          }}
          onPreview={setPreviewingPost}
        />
      )}
      {previewingPost && (
        <IGPostPreview item={previewingPost} token={token} onClose={() => setPreviewingPost(null)} />
      )}
    </div>
  );
}
